import React, { useEffect, useState, useMemo, useRef } from "react";
import io from "socket.io-client";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import API_BASE_URL, { API_ENDPOINTS, DOCS_URL, getBrandingLogoUrl } from './config';
import logger from './utils/logger';
import { useBranding } from './BrandingContext';
import { useTheme } from './ThemeContext';
import { useToast } from './ToastContext';
import { exportReportsCSV, exportReportsExcel, exportReportsPDF, exportReportsJSON } from './utils/reportExport';
import InitiateRequest from "./InitiateRequest";
import BulkRequest from "./BulkRequest";
import SupportHub from "./SupportHub";
import {
  PieChart, Pie, Legend, AreaChart, Area, BarChart, Bar, CartesianGrid,
  XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, Cell
} from 'recharts';
import {
  FiHome, FiFileText, FiPlusCircle, FiSettings, FiUser,
  FiLogOut, FiMenu, FiActivity, FiDownload, FiUsers, FiPenTool,
  FiChevronDown, FiChevronUp, FiCheckCircle, FiClock, FiFilter,
  FiCopy, FiBriefcase, FiCreditCard, FiLayout, FiTrash, FiLink,
  FiEdit2, FiX, FiCheck, FiChevronRight, FiCode, FiSend, FiRefreshCcw, FiXCircle,
  FiMoreVertical, FiEye, FiKey, FiShield, FiMail, FiLock, FiRadio, FiExternalLink, FiMessageSquare, FiInbox, FiUserPlus, FiBarChart2, FiSearch, FiSun, FiMoon, FiBell,
} from "react-icons/fi";
import SigningLoader from './SigningLoader';
import "./Dashboard.css";

// Custom Webhook Icon using the exact paths from the provided
// lucide `webhook` SVG, sized/aligned like the other sidebar icons.
const WebhookIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    fill="none"
    width="18"
    height="18"
    preserveAspectRatio="xMidYMid meet"
    style={{ display: "block" }}
  >
    <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" />
    <path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" />
    <path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" />
  </svg>
);

// Static CSS styles (defined outside component to avoid re-creation on every render)
const DASHBOARD_INLINE_STYLES = `
    .action-btn-group { display: flex; justify-content: center; gap: 8px; }
    .btn-action { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 6px 12px; font-size: 0.85rem; font-weight: 500; border-radius: 6px; text-decoration: none; transition: all 0.2s ease; border: 1px solid transparent; cursor: pointer; }
    .btn-action:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.4); }
    .btn-sign { background: rgba(16, 185, 129, 0.2); color: #6ee7b7; border: 1px solid rgba(16, 185, 129, 0.4); font-size: 0.8rem; padding: 4px 10px; }
    .btn-sign:hover { background: rgba(16, 185, 129, 0.3); }
    .btn-download { background: var(--black-bg-card-4); color: #93c5fd; border: 1px solid var(--black-border-light); }
    .btn-download:hover { background: var(--black-bg-card-3); color: #bfdbfe; border-color: var(--black-border-subtle); }
    .btn-audit { background: var(--black-bg-card-4); color: var(--text-on-black-secondary); border: 1px solid var(--black-border-light); }
    .btn-audit:hover { background: var(--black-bg-card-3); color: var(--text-on-black); border-color: var(--black-border-subtle); }
    .expand-btn { background: none; border: none; cursor: pointer; color: var(--text-on-black-muted); display: flex; align-items: center; font-size: 0.9rem; }
    .expand-btn:hover { color: var(--primary); }
    .expanded-row { background: var(--black-bg-card-2); border-top: 1px dashed var(--black-border); border-bottom: 1px solid var(--black-border); }
    .recipient-list-container { padding: 15px 30px; }
    .recipient-header { font-size: 0.85rem; text-transform: uppercase; color: var(--text-on-black-muted); font-weight: 700; margin-bottom: 10px; letter-spacing: 0.5px; }
    .recipient-grid { display: grid; grid-template-columns: 2fr 2fr 1.5fr 1fr; gap: 15px; align-items: center; padding: 10px; background: var(--black-bg-card-4); border: 1px solid var(--black-border); border-radius: 8px; margin-bottom: 8px; color: var(--text-on-black); }
    .recipient-status { display: flex; align-items: center; gap: 6px; font-size: 0.9rem; font-weight: 500; }
    .status-signed { color: #10b981; }
    .status-pending { color: #f59e0b; }
    .status-rejected { color: #EF4444; }
    .status-badge.rejected { background: #EF4444; color: #ffffff; }
    .dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
    .dot.signed { background: #10b981; }
    .dot.pending { background: #f59e0b; }
    .dot.rejected { background: #EF4444; }
    .table-row-main { cursor: pointer; }
    .table-row-main:hover { background-color: var(--black-bg-card-2); }
    .admin-select { padding: 6px 10px; border-radius: 8px; border: 1px solid var(--black-border-light); font-size: 14px; background: var(--black-bg-card-3); color: var(--text-on-black); margin-right: 0; }
    .webhook-split-container { display: flex; gap: 20px; transition: all 0.4s ease; margin-top: 20px; }
    .webhook-logs-left { flex: 1; transition: all 0.4s ease; }
    .webhook-logs-left.shrunk { flex: 0 0 380px; max-width: 380px; }
    .webhook-detail-middle { flex: 1; background: var(--black-bg-card-3); border-radius: 12px; border: 1px solid var(--black-border); padding: 25px; box-shadow: 0 4px 20px rgba(0,0,0,0.4); animation: slideIn 0.3s ease; height: fit-content; position: sticky; top: 20px; color: var(--text-on-black); }
    @keyframes slideIn { from { opacity: 0; transform: translateX(30px); } to { opacity: 1; transform: translateX(0); } }
    .json-pre { background: var(--black-bg-card-2); color: #38bdf8; padding: 20px; border-radius: 10px; font-family: 'Fira Code', monospace; font-size: 13px; overflow-x: auto; line-height: 1.6; border: 1px solid var(--black-border); max-height: 450px; white-space: pre-wrap; word-break: break-all; }
    .webhook-readonly-box { background: var(--black-bg-card-4); padding: 12px; border-radius: 8px; border: 1px solid var(--black-border); font-family: monospace; font-size: 13.5px; color: var(--text-on-black-secondary); word-break: break-all; }
    .btn-edit-webhook { display: inline-flex; align-items: center; gap: 8px; background: var(--primary); color: #111827; padding: 9px 18px; border-radius: 10px; font-size: 13.5px; cursor: pointer; border: 1px solid #eab308; font-weight: 700; box-shadow: 0 2px 8px rgba(0,0,0,0.18); }
    .btn-edit-webhook:hover { background: #ffea00; border-color: #ca8a04; transform: translateY(-1px); }
    .log-row { cursor: pointer; transition: 0.2s; border-left: 4px solid transparent; }
    .log-row:hover { background-color: var(--black-bg-card-2) !important; }
    .log-row.active { background-color: rgba(255, 214, 0, 0.08) !important; border-left-color: var(--primary); }
    .webhook-status-pill { padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 700; }
    .webhook-status-pill.success { background: rgba(16, 185, 129, 0.15); color: #10b981; }
    .webhook-status-pill.error { background: rgba(239, 68, 68, 0.15); color: #ef4444; }
    .btn-resend-webhook { display: flex; align-items: center; gap: 6px; background: #FFD600; color: var(--text-on-black); padding: 8px 14px; border: 1px solid #eab308; border-radius: 6px; font-size: 13px; font-weight: 700; cursor: pointer; }
    .btn-resend-webhook:disabled { opacity: 0.5; cursor: not-allowed; }
    .delete-modal-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.65); display: flex; align-items: center; justify-content: center; z-index: 200; }
    .delete-modal { background: var(--black-bg-card-2); color: var(--text-on-black); padding: 28px 30px 22px; border-radius: 16px; min-width: 400px; max-width: 500px; box-shadow: 0 25px 80px rgba(0,0,0,0.6); }
    .delete-modal h3 { margin: 0 0 12px 0; font-size: 20px; color: #fecaca; }
    .delete-modal p { margin: 0 0 20px 0; font-size: 14px; color: var(--text-on-black-secondary); line-height: 1.6; }
    .delete-modal-template-name { font-weight: 700; color: #facc15; word-break: break-word; }
    .delete-modal-actions { display: flex; justify-content: flex-end; gap: 12px; }
    .btn-delete-cancel { background: transparent; border: 1px solid var(--black-border-light); color: var(--text-on-black-secondary); padding: 10px 18px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
    .btn-delete-cancel:hover { background: var(--black-bg-card-2); border-color: var(--black-border); }
    .btn-delete-confirm { background: #dc2626; border: 1px solid #991b1b; color: var(--text-on-black); padding: 10px 18px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
    .btn-delete-confirm:hover { background: #b91c1c; border-color: #7f1d1d; }
`;

const Dashboard = () => {
  const toast = useToast();
  const [tab, setTab] = useState("overview");
  const [docs, setDocs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState({ total: 0, signed: 0, pending: 0, rejected: 0, completionRate: "0%" });
  const [statsPrevious, setStatsPrevious] = useState(null); // previous period for trend (null when ALL_TIME or unavailable)
  const [prevAvgTimeToSignHours, setPrevAvgTimeToSignHours] = useState(null);
  const [pieData, setPieData] = useState([]);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [topRecipients, setTopRecipients] = useState([]);
  const [templatesUsedData, setTemplatesUsedData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  // UI States
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [expandedDocId, setExpandedDocId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [cmdQuery, setCmdQuery] = useState('');
  const [cmdIdx, setCmdIdx] = useState(0);
  const cmdInputRef = useRef(null);

  // Filtering & Animation States
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [timeRange, setTimeRange] = useState("ALL_TIME"); // ALL_TIME, 7_DAYS, 30_DAYS, 90_DAYS

  // Pagination State
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [docsLoading, setDocsLoading] = useState(false);
  const perPage = rowsPerPage;

  // Inbox State (documents where current user is pending recipient)
  const [inboxDocs, setInboxDocs] = useState([]);
  const [inboxPage, setInboxPage] = useState(1);
  const [inboxTotalPages, setInboxTotalPages] = useState(1);
  const [inboxTotal, setInboxTotal] = useState(0);
  const [inboxUnseenCount, setInboxUnseenCount] = useState(0);

  // Contacts State
  const [contactsList, setContactsList] = useState([]);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '' });
  const [editingContactId, setEditingContactId] = useState(null);
  const [contactSaveMsg, setContactSaveMsg] = useState({ type: '', text: '' });
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactSearch, setContactSearch] = useState('');

  // Reports State
  const [reportsData, setReportsData] = useState({
    signing_velocity: [],
    avg_time_to_sign_hours: null,
    decline_rate_percent: null,
    templates_breakdown: [],
    monthly_volume: [],
  });
  const [reportsLoading, setReportsLoading] = useState(false);
  const [showReportsExportMenu, setShowReportsExportMenu] = useState(false);

  // Signing Links (open-link requests) State
  const [signingLinksDocs, setSigningLinksDocs] = useState([]);
  const [signingLinksPage, setSigningLinksPage] = useState(1);
  const [signingLinksTotalPages, setSigningLinksTotalPages] = useState(1);
  const [signingLinksTotal, setSigningLinksTotal] = useState(0);
  const [signingLinksLoading, setSigningLinksLoading] = useState(false);
  const [signingLinksToggling, setSigningLinksToggling] = useState(null);
  const [copiedLinkRefId, setCopiedLinkRefId] = useState(null);

  // Billing tab
  const [billingInfo, setBillingInfo] = useState({ credits: 0, plan: 'Starter', usage_this_month: 0 });
  const [billingLoading, setBillingLoading] = useState(false);

  // Global search (documents + templates)
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  // Webhooks State
  const [webhookConfig, setWebhookConfig] = useState({ url: '', headers: '{}', is_active: false });
  const [isEditingWebhook, setIsEditingWebhook] = useState(false);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [selectedWebhookLog, setSelectedWebhookLog] = useState(null);
  const [webhookSaveMsg, setWebhookSaveMsg] = useState("");
  const [resendingId, setResendingId] = useState(null);
  const [resendingNotifId, setResendingNotifId] = useState(null);

  // Settings: User Management State
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState("USER");
  const [newUserCompanyId, setNewUserCompanyId] = useState("");
  const [registerError, setRegisterError] = useState("");
  const [registerSuccess, setRegisterSuccess] = useState("");

  // Settings: Company Management State (Super Admin Only)
  const [newCompName, setNewCompName] = useState("");
  const [newCompSlug, setNewCompSlug] = useState("");
  const [newCompEmail, setNewCompEmail] = useState("");
  const [newCompAddress, setNewCompAddress] = useState("");
  const [compCreateMsg, setCompCreateMsg] = useState({ type: '', text: '' });

  // Settings: Add Credits (Super Admin Only)
  const [creditCompId, setCreditCompId] = useState("");
  const [creditsToAdd, setCreditsToAdd] = useState("");
  const [creditMsg, setCreditMsg] = useState({ type: '', text: '' });

  // Super Admin Specific State
  const [companies, setCompanies] = useState([]);
  const [selectedAdminCompany, setSelectedAdminCompany] = useState("");

  // Templates UI state
  const [openTemplateMenuId, setOpenTemplateMenuId] = useState(null);
  const [initialTemplateIdForInitiate, setInitialTemplateIdForInitiate] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ open: false, templateId: null, templateName: "" });

  const location = useLocation();
  const navigate = useNavigate();
  const { branding, refreshBranding, updateBranding, applyBrandingToDom } = useBranding();
  const { theme, toggleTheme } = useTheme();

  // Branding form (Settings – Admin only)
  const [brandingForm, setBrandingForm] = useState({
    product_name: '', logo_url: null, theme: 'default', primary_color: '#FFD600', accent_color: '#FFAB00',
    show_powered_by: true, first_part_color: '#ffffff', rest_part_color: null, custom_themes: [], custom_theme_name: '',
    text_logo_style: 'default', text_logo_custom_mode: 'single',
  });
  const [brandingSaveStatus, setBrandingSaveStatus] = useState({ type: '', message: '' });
  const [brandingUploading, setBrandingUploading] = useState(false);

  // Get user info from localStorage (must be before state that uses it)
  const userEmail = localStorage.getItem('email') || "User";
  const userRoles = JSON.parse(localStorage.getItem('roles') || "[]");
  const orgId = localStorage.getItem('orgId');

  // Settings: Enhanced UI State
  const [allUsers, setAllUsers] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [brandingFormDirty, setBrandingFormDirty] = useState(false);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [showAddThemeModal, setShowAddThemeModal] = useState(false);
  const [addThemeModalForm, setAddThemeModalForm] = useState({ name: '', primary_color: '#FFD600', accent_color: '#FFAB00' });
  const [profileData, setProfileData] = useState({ email: userEmail });
  const [changePasswordData, setChangePasswordData] = useState({ current: "", new: "", confirm: "" });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserData, setEditingUserData] = useState({});

  // Team Management Tab state
  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalMode, setUserModalMode] = useState('create');
  const [userForm, setUserForm] = useState({ email: '', password: '', role: 'USER', companyId: '' });
  const [userFormError, setUserFormError] = useState('');
  const [userFormSuccess, setUserFormSuccess] = useState('');
  const [userFormSaving, setUserFormSaving] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [teamSearch, setTeamSearch] = useState('');
  const [teamRoleFilter, setTeamRoleFilter] = useState('ALL');
  const [togglingUserId, setTogglingUserId] = useState(null);

  // Notification Center state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifSocketRef = useRef(null);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);

  const isSuperAdmin = userRoles.includes("SUPER_ADMIN");
  const isAdmin = userRoles.includes("ADMIN");
  const canAddUsers = isSuperAdmin || isAdmin;

  // FETCH CURRENT USER INFO (FOR CREDITS - Regular Users)
  const fetchUserInfo = async () => {
      try {
          const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
          const res = await axios.get(`${baseUrl}/auth/me`);
          if (res.data.user && res.data.user.credits !== undefined) {
              setAvailableCredits(res.data.user.credits);
          }
      } catch (err) {
          console.error("Failed to fetch user info", err);
      }
  };

  const fetchCompanies = async () => {
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      const res = await axios.get(`${baseUrl}/companies`);
      setCompanies(res.data.companies || []);
    } catch (err) {
      console.error("Failed to fetch companies", err);
    }
  };

  useEffect(() => {
    fetchUserInfo(); // Fetch on load
    fetchUnreadCount(); // Load notification badge on mount
    fetchNotifications(); // Pre-load notifications so bell is instant
    if (isSuperAdmin) {
      fetchCompanies();
    }
    // Poll unread count every 30s for freshness
    const pollInterval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(pollInterval);
  }, [isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Socket connection for real-time notifications
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    const socket = io(API_BASE_URL);
    notifSocketRef.current = socket;
    socket.emit('join', {
      user_id: userId,
      company_id: localStorage.getItem('orgId') || '',
      roles: userRoles,
    });
    socket.on('new_notification', (notif) => {
      setUnreadCount(c => c + 1);
      setNotifications(prev => [notif, ...prev].slice(0, 50));
    });
    return () => {
      socket.off('new_notification');
      socket.disconnect();
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      // Cmd+K / Ctrl+K → command palette (allowed from inputs too)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdQuery('');
        setCmdIdx(0);
        setShowCommandPalette(v => !v);
        return;
      }
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'n' || e.key === 'N') { setTab('initiate'); toast('New Request', 'info'); }
      else if (e.key === 't' || e.key === 'T') { setTab('templates'); toast('Templates', 'info'); }
      else if (e.key === 's' || e.key === 'S') { setTab('support'); toast('Support', 'info'); }
      else if (e.key === 'o' || e.key === 'O') { setTab('overview'); toast('Overview', 'info'); }
      else if (e.key === '?') { setShowShortcutsModal(v => !v); }
      else if (e.key === 'Escape') { setShowShortcutsModal(false); setSidebarOpen(false); setShowCommandPalette(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toast]);

  // Read query params (e.g. deep-links like ?tab=templates)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    const templateIdParam = params.get("templateId");

    if (tabParam) {
      setTab(tabParam);
    }
    if (templateIdParam) {
      setInitialTemplateIdForInitiate(templateIdParam);
    }
  }, [location.search]);

  // UPDATE: Added selectedAdminCompany and timeRange dependency to trigger re-fetches
  useEffect(() => {
    if (tab === "overview") {
      load(1, { includeAnalytics: true });
      setPage(1);
    }
    if (tab === "contracts") {
      load(1, { includeAnalytics: false });
      setPage(1);
    }
    if (tab === "overview") {
      loadReports();
    }
    if (tab === "inbox") {
      loadInbox(1);
      setInboxPage(1);
    }
    if (tab === "contacts") {
      loadContacts();
    }
    if (tab === "reports") {
      loadReports();
    } else {
      setShowReportsExportMenu(false);
    }
    if (tab === "templates") {
        loadTemplates();
    }
    if (tab === "webhooks") {
        // Reset webhook state when switching companies
        setWebhookConfig({ url: '', headers: '{}', is_active: false });
        setWebhookLogs([]);

        // Only fetch if a specific company is selected (or if user is not super admin)
        if (!isSuperAdmin || selectedAdminCompany) {
            fetchWebhookConfig();
            fetchWebhookLogs();
        }
    }
    if (tab === "signingLinks") {
        loadSigningLinks(1);
        setSigningLinksPage(1);
    }
    if (tab === "settings") {
        if (isSuperAdmin) {
            fetchCompanies();
        }
    }
    if (tab === "team") {
        if (isSuperAdmin || isAdmin) {
            fetchUsers();
        }
    }
    if (tab === "billing" || tab === "overview") {
        fetchBilling();
    }
  }, [tab, selectedAdminCompany, timeRange, rowsPerPage]);

  // Load inbox count once for sidebar notification badge
  useEffect(() => {
    if (!isSuperAdmin || selectedAdminCompany) loadInbox(1);
  }, [isSuperAdmin, selectedAdminCompany]);

  // When user opens inbox tab, mark all as "seen" so badge clears
  useEffect(() => {
    if (tab === 'inbox' && inboxTotal > 0) {
      localStorage.setItem('inbox_last_seen_total', String(inboxTotal));
      setInboxUnseenCount(0);
    }
  }, [tab, inboxTotal]);

  // Load search data on mount (templates + contacts only; docs load via tab effect)
  useEffect(() => {
    loadTemplates();
    loadContacts();
  }, []);

  // Refresh branding when Dashboard mounts or when super admin switches company (ensures logo persists after refresh/login)
  useEffect(() => {
    refreshBranding(isSuperAdmin ? selectedAdminCompany || null : null);
  }, [refreshBranding, isSuperAdmin, selectedAdminCompany]);

  // Theme presets for inference and UI (same order)
  const THEME_PRESETS = [
    { id: 'default', label: 'Default', primary: '#FFD600', accent: '#FFAB00' },
    { id: 'ocean', label: 'Ocean', primary: '#0ea5e9', accent: '#06b6d4' },
    { id: 'forest', label: 'Forest', primary: '#10b981', accent: '#059669' },
    { id: 'slate', label: 'Slate', primary: '#64748b', accent: '#475569' },
  ];
  const inferTheme = (primary, accent) => {
    const p = (primary || '').toLowerCase().replace(/#/g, '');
    const a = (accent || '').toLowerCase().replace(/#/g, '');
    const found = THEME_PRESETS.find(x =>
      (x.primary.toLowerCase().replace(/#/g, '') === p && x.accent.toLowerCase().replace(/#/g, '') === a));
    return found ? found.id : 'custom';
  };

  // Sync branding form when opening settings (admin) – infer theme from applied colors so selection always matches (fixes Ocean showing as Default)
  useEffect(() => {
    if (tab === 'settings' && (isAdmin || isSuperAdmin)) {
      const effectiveTheme = inferTheme(branding.primary_color, branding.accent_color);
      const fFirst = branding.first_part_color ?? null;
        const fRest = branding.rest_part_color ?? null;
        const isDefaultStyle = fFirst == null && fRest == null;
        const isSingle = fFirst != null && fRest != null && fFirst === fRest;
        setBrandingForm({
          product_name: branding.product_name || 'SignTada',
          logo_url: branding.logo_url ?? null,
          theme: effectiveTheme,
          primary_color: branding.primary_color || '#FFD600',
          accent_color: branding.accent_color || '#FFAB00',
          show_powered_by: true,
          first_part_color: fFirst ?? '#ffffff',
          rest_part_color: fRest ?? (branding.primary_color || '#FFD600'),
          custom_themes: Array.isArray(branding.custom_themes) ? branding.custom_themes : [],
          text_logo_style: isDefaultStyle ? 'default' : 'custom',
          text_logo_custom_mode: isSingle ? 'single' : 'dual',
        });
        setBrandingFormDirty(false);
      }
    }, [tab, isAdmin, isSuperAdmin, branding.product_name, branding.logo_url, branding.theme, branding.primary_color, branding.accent_color, branding.first_part_color, branding.rest_part_color, branding.custom_themes]);

  // Clear "Saved." / error message when user starts editing again
  useEffect(() => {
    if (brandingFormDirty && brandingSaveStatus.message) {
      setBrandingSaveStatus({ type: '', message: '' });
    }
  }, [brandingFormDirty]);

  // Helper function to get date range
  const getDateRange = () => {
    const now = new Date();
    let dateFrom = null;
    
    switch (timeRange) {
      case "7_DAYS":
        dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "30_DAYS":
        dateFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case "90_DAYS":
        dateFrom = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        return { date_from: null, date_to: null };
    }
    
    return {
      date_from: dateFrom ? dateFrom.toISOString() : null,
      date_to: now.toISOString()
    };
  };

  // Previous period (same length, immediately before current period) for trend comparison
  const getPreviousDateRange = () => {
    const now = new Date();
    let dateFrom = null;
    let dateTo = null;
    const dayMs = 24 * 60 * 60 * 1000;
    switch (timeRange) {
      case "7_DAYS":
        dateTo = new Date(now.getTime() - 7 * dayMs);
        dateFrom = new Date(now.getTime() - 14 * dayMs);
        break;
      case "30_DAYS":
        dateTo = new Date(now.getTime() - 30 * dayMs);
        dateFrom = new Date(now.getTime() - 60 * dayMs);
        break;
      case "90_DAYS":
        dateTo = new Date(now.getTime() - 90 * dayMs);
        dateFrom = new Date(now.getTime() - 180 * dayMs);
        break;
      default:
        return { date_from: null, date_to: null };
    }
    return {
      date_from: dateFrom.toISOString(),
      date_to: dateTo.toISOString()
    };
  };

  const load = async (pageNum, options = { includeAnalytics: true }) => {
    setDocsLoading(true);
    try {
      const includeAnalytics = options?.includeAnalytics !== false;
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      const dateRange = getDateRange();

      const baseParams = {};
      if (isSuperAdmin && selectedAdminCompany) baseParams.orgId = selectedAdminCompany;
      if (dateRange.date_from) baseParams.date_from = dateRange.date_from;
      if (dateRange.date_to) baseParams.date_to = dateRange.date_to;
      const statusForQuery = options?.statusOverride !== undefined ? options.statusOverride : filterStatus;
      if (statusForQuery && statusForQuery !== 'ALL') baseParams.status = statusForQuery;

      if (!includeAnalytics) {
        const docsRes = await axios.get(`${baseUrl}/requests/list_requests`, {
          params: { ...baseParams, page: pageNum, per_page: perPage },
        });
        const payload = docsRes.data || {};
        const data = Array.isArray(payload.data) ? payload.data : [];
        const total = Number(payload.total || 0);
        const backendPage = Number(payload.page || pageNum);
        const backendTotalPages = Number(
          payload.total_pages || Math.max(1, Math.ceil(total / (payload.per_page || perPage)))
        );
        setDocs(data);
        setTotalDocs(total);
        setPage(backendPage);
        setTotalPages(backendTotalPages);
        return;
      }

      fetchUserInfo(); // Refresh credits for regular users (fire-and-forget)
      if (isSuperAdmin) fetchCompanies(); // Refresh credits for Super Admins (fire-and-forget)

      // Fire all overview APIs in parallel.
      const prevRange = getPreviousDateRange();
      const [statsRes, prevStatsRes, docsRes, topRecipientsRes, activityRes] = await Promise.allSettled([
        axios.get(`${baseUrl}/requests/stats`, { params: baseParams }),
        prevRange.date_from && prevRange.date_to
          ? axios.get(`${baseUrl}/requests/stats`, { params: { ...baseParams, date_from: prevRange.date_from, date_to: prevRange.date_to } })
          : Promise.resolve(null),
        axios.get(`${baseUrl}/requests/list_requests`, { params: { ...baseParams, page: pageNum, per_page: perPage } }),
        axios.get(`${baseUrl}/requests/top_recipients`, { params: { ...baseParams, limit: 5 } }),
        axios.get(`${baseUrl}/requests/recent_activity`, { params: { ...baseParams, limit: 6 } }),
      ]);

      // Process stats
      if (statsRes.status === 'fulfilled') {
        const sd = statsRes.value.data;
        setStats(sd);
        setPieData([
          { name: 'Signed', value: sd.signed, color: '#10B981' },
          { name: 'Pending', value: sd.pending, color: '#D97706' },
          { name: 'Rejected', value: sd.rejected || 0, color: '#EF4444' },
        ]);
        if (sd.templates_used !== undefined) {
          setTemplatesUsedData([
            { name: 'Templates Used', value: sd.templates_used, color: '#10B981' },
            { name: 'Custom Documents', value: sd.total - sd.templates_used, color: '#94A3B8' }
          ]);
        }
      }

      // Process previous stats
      setStatsPrevious(prevStatsRes.status === 'fulfilled' && prevStatsRes.value ? prevStatsRes.value.data : null);

      // Process documents
      if (docsRes.status === 'fulfilled') {
        const payload = docsRes.value.data || {};
        const data = Array.isArray(payload.data) ? payload.data : [];
        const total = Number(payload.total || 0);
        const backendPage = Number(payload.page || pageNum);
        const backendTotalPages = Number(
          payload.total_pages || Math.max(1, Math.ceil(total / (payload.per_page || perPage)))
        );
        setDocs(data);
        setTotalDocs(total);
        setPage(backendPage);
        setTotalPages(backendTotalPages);
      }

      // Process top recipients
      setTopRecipients(topRecipientsRes.status === 'fulfilled' ? (topRecipientsRes.value.data.recipients || []) : []);

      // Process recent activity
      setRecentActivity(activityRes.status === 'fulfilled' ? (activityRes.value.data.activities || []) : []);
    } catch (e) {
      console.error("load failed", e);
    } finally {
      setDocsLoading(false);
    }
  };

  // UPDATE: Pass selectedAdminCompany as orgId for Templates
  const loadInbox = async (pageNum) => {
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      const params = { page: pageNum, per_page: perPage };
      if (isSuperAdmin && selectedAdminCompany) params.orgId = selectedAdminCompany;
      const res = await axios.get(`${baseUrl}/requests/inbox`, { params });
      const { data, total, page: backendPage, total_pages: backendTotalPages } = res.data || {};
      setInboxDocs(data || []);
      setInboxTotal(total || 0);
      setInboxPage(Number(backendPage || pageNum));
      setInboxTotalPages(Number(backendTotalPages || Math.max(1, Math.ceil((total || 0) / perPage))));
      // Calculate unseen: compare with last seen count
      const lastSeen = parseInt(localStorage.getItem('inbox_last_seen_total') || '0', 10);
      setInboxUnseenCount(Math.max(0, (total || 0) - lastSeen));
    } catch (e) {
      setInboxDocs([]);
      setInboxTotal(0);
      setInboxTotalPages(0);
    }
  };

  const loadSigningLinks = async (pageNum) => {
    setSigningLinksLoading(true);
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      const params = { page: pageNum, per_page: perPage, open_link: 'true' };
      if (isSuperAdmin && selectedAdminCompany) params.orgId = selectedAdminCompany;
      const res = await axios.get(`${baseUrl}/requests/list_requests`, { params });
      const { data, total, page: backendPage, total_pages: backendTotalPages } = res.data || {};
      setSigningLinksDocs(data || []);
      setSigningLinksTotal(total || 0);
      setSigningLinksPage(Number(backendPage || pageNum));
      setSigningLinksTotalPages(Number(backendTotalPages || Math.max(1, Math.ceil((total || 0) / perPage))));
    } catch (e) {
      setSigningLinksDocs([]);
      setSigningLinksTotal(0);
      setSigningLinksTotalPages(0);
    } finally {
      setSigningLinksLoading(false);
    }
  };

  const toggleOpenLinkEnabled = async (referenceId, currentEnabled) => {
    setSigningLinksToggling(referenceId);
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      await axios.patch(`${baseUrl}/requests/${referenceId}/open_link`, { enabled: !currentEnabled });
      await loadSigningLinks(signingLinksPage);
    } catch (e) {
      console.error('Toggle open link failed', e);
      toast(e.response?.data?.error || 'Failed to update link', 'error');
    } finally {
      setSigningLinksToggling(null);
    }
  };

  const fetchBilling = async () => {
    setBillingLoading(true);
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      const params = {};
      if (isSuperAdmin && selectedAdminCompany) params.orgId = selectedAdminCompany;
      const res = await axios.get(`${baseUrl}/companies/billing`, { params });
      setBillingInfo({
        credits: res.data.credits ?? 0,
        plan: res.data.plan || 'Starter',
        usage_this_month: res.data.usage_this_month ?? 0,
      });
    } catch (e) {
      setBillingInfo({ credits: 0, plan: 'Starter', usage_this_month: 0 });
    } finally {
      setBillingLoading(false);
    }
  };

  const copySigningLink = (referenceId, openLinkToken) => {
    const url = `${window.location.origin}/sign/${referenceId}?link=${encodeURIComponent(openLinkToken)}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLinkRefId(referenceId);
      setTimeout(() => setCopiedLinkRefId(null), 2000);
    }).catch(() => {});
  };

  const loadContacts = async () => {
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      const params = {};
      if (isSuperAdmin && selectedAdminCompany) params.orgId = selectedAdminCompany;
      const res = await axios.get(`${baseUrl}/contacts`, { params });
      setContactsList(res.data);
    } catch (e) {
      setContactsList([]);
    }
  };

  const loadReports = async () => {
    setReportsLoading(true);
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      const dateRange = getDateRange();
      const params = {};
      if (isSuperAdmin && selectedAdminCompany) params.orgId = selectedAdminCompany;
      if (dateRange.date_from) params.date_from = dateRange.date_from;
      if (dateRange.date_to) params.date_to = dateRange.date_to;
      const res = await axios.get(`${baseUrl}/requests/reports`, { params });
      setReportsData(res.data);

      const prevRange = getPreviousDateRange();
      if (prevRange.date_from && prevRange.date_to) {
        try {
          const prevParams = { ...params, date_from: prevRange.date_from, date_to: prevRange.date_to };
          const prevRes = await axios.get(`${baseUrl}/requests/reports`, { params: prevParams });
          setPrevAvgTimeToSignHours(prevRes.data?.avg_time_to_sign_hours ?? null);
        } catch {
          setPrevAvgTimeToSignHours(null);
        }
      } else {
        setPrevAvgTimeToSignHours(null);
      }
    } catch (e) {
      setReportsData({ signing_velocity: [], avg_time_to_sign_hours: null, decline_rate_percent: null, templates_breakdown: [], monthly_volume: [] });
      setPrevAvgTimeToSignHours(null);
    } finally {
      setReportsLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSaveMsg({ type: '', text: '' });
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    const payload = { name: contactForm.name.trim(), email: contactForm.email.trim().toLowerCase(), phone: (contactForm.phone || '').trim() };
    if (isSuperAdmin && selectedAdminCompany) payload.org_id = selectedAdminCompany;
    try {
      if (editingContactId) {
        await axios.put(`${baseUrl}/contacts/${editingContactId}`, payload);
        setContactSaveMsg({ type: 'success', text: 'Contact updated.' });
      } else {
        await axios.post(`${baseUrl}/contacts`, payload);
        setContactSaveMsg({ type: 'success', text: 'Contact added.' });
      }
      setShowContactModal(false);
      setEditingContactId(null);
      setContactForm({ name: '', email: '', phone: '' });
      loadContacts();
      setTimeout(() => setContactSaveMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setContactSaveMsg({ type: 'error', text: err.response?.data?.error || 'Failed to save contact.' });
    }
  };

  const handleContactDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      await axios.delete(`${baseUrl}/contacts/${id}`);
      loadContacts();
      setShowContactModal(false);
      setEditingContactId(null);
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to delete.', 'error');
    }
  };

  const openContactModal = (contact) => {
    if (contact) {
      setEditingContactId(contact._id);
      setContactForm({ name: contact.name || '', email: contact.email || '', phone: contact.phone || '' });
    } else {
      setEditingContactId(null);
      setContactForm({ name: '', email: '', phone: '' });
    }
    setShowContactModal(true);
    setContactSaveMsg({ type: '', text: '' });
  };

  const loadTemplates = async () => {
    try {
        const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
        const params = {};
        if (isSuperAdmin && selectedAdminCompany) {
            params.orgId = selectedAdminCompany;
        }
        const res = await axios.get(`${baseUrl}/templates/list`, { params });
        setTemplates(res.data);
    } catch (e) {
        console.error("load templates failed", e);
    }
  }

  const handleDeleteTemplate = async (id) => {
    try {
        const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
        await axios.delete(`${baseUrl}/templates/${id}`);
        loadTemplates();
        setDeleteConfirmModal({ open: false, templateId: null, templateName: "" });
    } catch (e) { 
        console.error(e);
        toast("Failed to delete template. Please try again.", 'error');
    }
  }

  // UPDATE: Pass selectedAdminCompany as companyId for Webhooks
  const fetchWebhookConfig = async () => {
    try {
        if (isSuperAdmin && !selectedAdminCompany) return;

        const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
        const params = {};
        if (selectedAdminCompany) params.companyId = selectedAdminCompany;

        const res = await axios.get(`${baseUrl}/webhooks`, { params });
        setWebhookConfig({
            ...res.data,
            headers: JSON.stringify(res.data.headers || {}, null, 2)
        });
    } catch (e) {
        console.error(e);
        // Reset to default on error/404
        setWebhookConfig({ url: '', headers: '{}', is_active: false });
    }
  };

  // UPDATE: Pass selectedAdminCompany as companyId for Webhooks Logs
  const fetchWebhookLogs = async () => {
    try {
        if (isSuperAdmin && !selectedAdminCompany) return;

        const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
        const params = {};
        if (selectedAdminCompany) params.companyId = selectedAdminCompany;

        const res = await axios.get(`${baseUrl}/webhooks/logs`, { params });
        setWebhookLogs(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSaveWebhook = async (e) => {
    e.preventDefault();
    if (isSuperAdmin && !selectedAdminCompany) {
        toast("Please select a company from the top dropdown first.", 'error');
        return;
    }

    try {
        const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
        let parsedHeaders = {};
        try {
            parsedHeaders = JSON.parse(webhookConfig.headers);
        } catch(e) {
            toast("Invalid JSON in headers field", 'error');
            return;
        }

        const payload = {
            url: webhookConfig.url,
            is_active: webhookConfig.is_active,
            headers: parsedHeaders
        };
        // Add companyId to payload for Super Admin
        if (selectedAdminCompany) payload.companyId = selectedAdminCompany;

        await axios.post(`${baseUrl}/webhooks`, payload);
        setWebhookSaveMsg("Webhook configuration updated!");
        setIsEditingWebhook(false);
        setTimeout(() => setWebhookSaveMsg(""), 3000);
    } catch (e) { toast("Failed to save webhook", 'error'); }
  };

  const handleResendWebhook = async (logId) => {
    setResendingId(logId);
    try {
        const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
        const payload = { log_id: logId };
        // Add companyId to payload for Super Admin
        if (selectedAdminCompany) payload.companyId = selectedAdminCompany;

        await axios.post(`${baseUrl}/webhooks/resend`, payload);
        toast("Payload resent successfully.", 'success');
        fetchWebhookLogs();
    } catch (err) {
        toast(err.response?.data?.error || "Failed to resend.", 'error');
    } finally {
        setResendingId(null);
    }
  };

  const handleResendNotification = async (referenceId) => {
    setResendingNotifId(referenceId);
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      await axios.post(`${baseUrl}/requests/resend_notification`, { reference_id: referenceId });
      toast("Notification resent to pending recipients.", 'success');
    } catch (err) {
      toast(err.response?.data?.error || "Failed to resend notification.", 'error');
    } finally {
      setResendingNotifId(null);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      load(newPage, { includeAnalytics: tab === "overview", statusOverride: filterStatus });
    }
  };

  const toggleRow = (refId) => {
    setExpandedDocId(expandedDocId === refId ? null : refId);
  };

  const getDownloadLink = (doc) => {
    const path = doc.last_signed_file_path || doc.file_path;
    if (!path) return null;
    if (typeof path === 'object' && path.url) return path.url;
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    return `${baseUrl}/files/uploads/${path}`;
  };

  const getAuditTrailLink = (refId) => {
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    return `${baseUrl}/signing/download_audit/${refId}`;
  };

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    try {
        if(refreshToken) {
            await axios.post(`${baseUrl}/auth/logout`, { refreshToken });
        }
    } catch (e) {
        console.error("Logout failed on server", e);
    }
    localStorage.clear();
    navigate("/login");
  };

  const handleRegisterUser = async (e) => {
    e.preventDefault();
    setRegisterError(""); setRegisterSuccess("");
    let finalCompanyId = orgId;
    if (isSuperAdmin) {
        if (newUserRole === 'SUPER_ADMIN') {
            finalCompanyId = null;
        } else {
            finalCompanyId = newUserCompanyId;
            if (!finalCompanyId) {
                setRegisterError("Please select a company for this user.");
                return;
            }
        }
    } else if (isAdmin) {
        // Admin can only create users in their own company
        finalCompanyId = orgId;
    }
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      await axios.post(`${baseUrl}/users`, {
        email: newUserEmail, password: newUserPassword, companyId: finalCompanyId, roles: [newUserRole]
      });
      setRegisterSuccess(`User ${newUserEmail} created successfully!`);
      setNewUserEmail(""); setNewUserPassword(""); setNewUserRole("USER"); setNewUserCompanyId("");
      fetchUsers(); // Refresh user list
    } catch (err) {
      setRegisterError(err.response?.data?.error || "Failed to create user");
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setCompCreateMsg({ type: '', text: '' });
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      await axios.post(`${baseUrl}/companies`, {
        name: newCompName, slug: newCompSlug, email: newCompEmail, address: newCompAddress
      });
      setCompCreateMsg({ type: 'success', text: `Company "${newCompName}" created!` });
      setNewCompName(""); setNewCompSlug(""); setNewCompEmail(""); setNewCompAddress("");
      fetchCompanies();
    } catch (err) {
      setCompCreateMsg({ type: 'error', text: err.response?.data?.error || "Failed to create company" });
    }
  };

  const handleAddCredits = async (e) => {
      e.preventDefault();
      setCreditMsg({ type: '', text: ''});
      try {
          const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
          const res = await axios.post(`${baseUrl}/companies/add_credits`, {
              companyId: creditCompId, credits: creditsToAdd
          });
          setCreditMsg({ type: 'success', text: res.data.message });
          setCreditsToAdd(""); setCreditCompId("");
          fetchCompanies();
      } catch (err) {
          setCreditMsg({ type: 'error', text: err.response?.data?.error || "Failed to add credits" });
      }
  };

  const fetchUsers = async () => {
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      const params = {};
      if (isSuperAdmin && selectedAdminCompany) {
        params.companyId = selectedAdminCompany;
      }
      const res = await axios.get(`${baseUrl}/users`, { params });
      setAllUsers(res.data.users || []);
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };


  const handleUpdateUser = async (userId) => {
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      await axios.put(`${baseUrl}/users/${userId}`, editingUserData);
      setEditingUserId(null);
      setEditingUserData({});
      fetchUsers();
      toast("User updated successfully", 'success');
    } catch (err) {
      toast(err.response?.data?.error || "Failed to update user", 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      await axios.delete(`${baseUrl}/users/${userId}`);
      fetchUsers();
    } catch (err) {
      toast(err.response?.data?.error || "Failed to delete user", 'error');
    }
  };

  // Team Management helpers
  const openCreateUserModal = () => {
    setUserForm({ email: '', password: '', role: 'USER', companyId: isSuperAdmin ? '' : (orgId || '') });
    setUserModalMode('create');
    setUserFormError('');
    setUserFormSuccess('');
    setInviteLink('');
    setEditingUserId(null);
    setShowUserModal(true);
  };

  const openEditUserModal = (u) => {
    setUserForm({ email: u.email, password: '', role: u.roles[0] || 'USER', companyId: u.companyId || '' });
    setEditingUserId(u.id);
    setUserModalMode('edit');
    setUserFormError('');
    setUserFormSuccess('');
    setShowUserModal(true);
  };

  const handleUserModalSubmit = async (e) => {
    e.preventDefault();
    setUserFormSaving(true);
    setUserFormError('');
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    try {
      if (userModalMode === 'create') {
        const payload = { email: userForm.email, roles: [userForm.role] };
        if (isSuperAdmin && userForm.companyId) payload.companyId = userForm.companyId;
        const res = await axios.post(`${baseUrl}/users`, payload);
        setUserFormSuccess('Invite sent successfully.');
        fetchUsers();
        if (res.data.inviteLink) {
          setInviteLink(res.data.inviteLink);
        }
      } else {
        const payload = { email: userForm.email, roles: [userForm.role] };
        if (userForm.password) payload.password = userForm.password;
        await axios.put(`${baseUrl}/users/${editingUserId}`, payload);
        setUserFormSuccess('User updated.');
        fetchUsers();
        setTimeout(() => { setShowUserModal(false); setUserFormSuccess(''); setEditingUserId(null); }, 1200);
      }
    } catch (err) {
      setUserFormError(err.response?.data?.error || 'Failed to save user.');
    } finally {
      setUserFormSaving(false);
    }
  };

  const handleToggleUserActive = async (userId, currentIsActive) => {
    setTogglingUserId(userId);
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    try {
      await axios.put(`${baseUrl}/users/${userId}`, { isActive: !currentIsActive });
      fetchUsers();
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to update user status.', 'error');
    } finally {
      setTogglingUserId(null);
    }
  };

  const getFilteredUsers = () => {
    return (allUsers || []).filter(u => {
      const q = teamSearch.trim().toLowerCase();
      const matchSearch = !q || u.email.toLowerCase().includes(q) || (u.roles[0] || '').toLowerCase().includes(q);
      const matchRole = teamRoleFilter === 'ALL' || (u.roles[0] || '') === teamRoleFilter;
      return matchSearch && matchRole;
    });
  };

  // Notification Center helpers
  const fetchNotifications = async () => {
    setNotifLoading(true);
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    try {
      const res = await axios.get(`${baseUrl}/notifications`, { params: { per_page: 8 } });
      setNotifications(res.data.notifications || []);
    } catch (e) {
      console.error('Failed to fetch notifications', e);
    } finally {
      setNotifLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    try {
      const res = await axios.get(`${baseUrl}/notifications/unread-count`);
      setUnreadCount(res.data.count || 0);
    } catch (e) { /* silent */ }
  };

  const handleMarkNotifRead = async (notif) => {
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    try {
      await axios.patch(`${baseUrl}/notifications/${notif.id}/read`);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
      setShowNotifDropdown(false);
      if (notif.link) {
        if (notif.link.startsWith('/dashboard?')) {
          const tabMatch = notif.link.match(/tab=([^&]+)/);
          if (tabMatch) setTab(tabMatch[1]);
        } else if (notif.link.startsWith('/documents/')) {
          navigate(notif.link);
        }
      }
    } catch (e) { /* silent */ }
  };

  const handleMarkAllRead = async () => {
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    try {
      await axios.patch(`${baseUrl}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const notifTimeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast("Copied to clipboard!", 'success');
  };

  const getRecipientStatusValue = (recipient) => {
    const normalized = String(recipient?.status || '').trim().toLowerCase();
    if (normalized === 'signed' || normalized === 'completed') return 'Signed';
    if (normalized === 'rejected') return 'Rejected';
    if (recipient?.signed) return 'Signed';
    if (recipient?.rejected) return 'Rejected';
    return 'Pending';
  };

  const getRecipientBadgeMeta = (recipient) => {
    const status = getRecipientStatusValue(recipient);
    if (status === 'Signed') return { className: 'signed', label: 'Signed' };
    if (status === 'Rejected') return { className: 'rejected', label: 'Rejected' };
    return { className: 'pending', label: 'Pending' };
  };

  const filteredDocs = useMemo(() => {
    if (filterStatus === "ALL") return docs;
    if (filterStatus === "SIGNED") {
      return docs.filter(d => {
        const recipients = d.recipients || [];
        const totalRecipients = recipients.length;
        const signedRecipients = recipients.filter(r => getRecipientStatusValue(r) === 'Signed').length;
        return d.status === "Signed" || (totalRecipients > 0 && signedRecipients === totalRecipients);
      });
    }
    if (filterStatus === "PENDING") return docs.filter(d => {
      const recipients = d.recipients || [];
      const hasRejected = recipients.some(r => getRecipientStatusValue(r) === 'Rejected');
      const hasSigned = recipients.some(r => getRecipientStatusValue(r) === 'Signed');
      return d.status !== "Signed" && !hasRejected && !hasSigned;
    });
    if (filterStatus === "REJECTED") {
      return docs.filter(d => {
        const recipients = d.recipients || [];
        return recipients.some(r => getRecipientStatusValue(r) === 'Rejected');
      });
    }
    return docs;
  }, [docs, filterStatus]);

  // Global search: 1) Nav by keyword, 2) Documents (ref, email, name, status), 3) Contacts (name, email)
  const NAV_KEYWORDS = [
    { keys: ['new'], label: 'New Request', tab: 'initiate', icon: FiPlusCircle },
    { keys: ['temp'], label: 'Templates', tab: 'templates', icon: FiLayout },
    { keys: ['contact'], label: 'Contacts', tab: 'contacts', icon: FiUserPlus },
    { keys: ['bill'], label: 'Billing', tab: 'billing', icon: FiCreditCard },
  ];
  const searchResults = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    const nav = q.length >= 1
      ? NAV_KEYWORDS.filter((n) => n.keys.some((k) => k.includes(q) || q.includes(k)))
      : [];
    const docMatches = (docs || []).filter((d) => {
      if (!q || q.length < 1) return false;
      const ref = (d.reference_id || d.referenceId || '').toLowerCase();
      const name = (d.document_name || '').toLowerCase();
      const status = (d.status || '').toLowerCase();
      const recipients = d.recipients || [];
      const recipientMatch = recipients.some((r) => (r.email || '').toLowerCase().includes(q));
      return ref.includes(q) || name.includes(q) || status.includes(q) || recipientMatch;
    }).slice(0, 6);
    const contactMatches = (contactsList || []).filter((c) => {
      if (!q || q.length < 1) return false;
      const name = (c.name || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    }).slice(0, 5);
    return { nav, documents: docMatches, contacts: contactMatches };
  }, [searchQuery, docs, contactsList]);

  const getDocumentsHeaderText = () => {
    if (tab === 'overview') return 'Recent Documents';
    if (filterStatus === 'SIGNED') return 'Signed Documents';
    if (filterStatus === 'PENDING') return 'Pending Documents';
    if (filterStatus === 'REJECTED') return 'Rejected Documents';
    return 'All Documents';
  };

  // HELPER: Logic to calculate which credits to show
  const getDisplayCredits = () => {
      if (!isSuperAdmin) {
          // Regular user or Company Admin - use own credits
          return availableCredits;
      }

      if (!selectedAdminCompany) {
          // Super Admin: "All Companies" selected - Sum of all credits
          return companies.reduce((acc, curr) => acc + (curr.credits || 0), 0).toFixed(1);
      } else {
          // Super Admin: Specific Company selected
          const selected = companies.find(c => c.id === selectedAdminCompany);
          return selected ? (selected.credits || 0) : 0;
      }
  };

  const styles = DASHBOARD_INLINE_STYLES;

  // Close template menu when clicking outside
  useEffect(() => {
    if (!openTemplateMenuId) return;
    const handleClick = (event) => {
      const target = event.target;
      if (target && !target.closest(".template-menu")) {
        setOpenTemplateMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openTemplateMenuId]);

  return (
    <div className="app-container">
      <style>{styles}</style>

      {/* Keyboard shortcuts modal */}
      {showShortcutsModal && (
        <div className="kbd-modal-overlay" onClick={() => setShowShortcutsModal(false)}>
          <div className="kbd-modal" onClick={e => e.stopPropagation()}>
            <h3>Keyboard Shortcuts</h3>
            <div className="kbd-row"><span>Command palette</span><span><kbd>⌘</kbd><kbd>K</kbd></span></div>
            <div className="kbd-row"><span>Overview</span><kbd>O</kbd></div>
            <div className="kbd-row"><span>New Request</span><kbd>N</kbd></div>
            <div className="kbd-row"><span>Templates</span><kbd>T</kbd></div>
            <div className="kbd-row"><span>Support</span><kbd>S</kbd></div>
            <div className="kbd-row"><span>Show shortcuts</span><kbd>?</kbd></div>
            <div className="kbd-row"><span>Close / dismiss</span><kbd>Esc</kbd></div>
          </div>
        </div>
      )}

      {/* Command palette (Cmd+K) */}
      {showCommandPalette && (() => {
        const primaryColor = branding.primary_color || '#FFD600';
        const allCmds = [
          { icon: <FiHome size={15} />, label: 'Dashboard', desc: 'Go to Overview', action: () => setTab('overview') },
          { icon: <FiPlusCircle size={15} />, label: 'New Request', desc: 'Initiate a new signing request', action: () => setTab('initiate') },
          { icon: <FiInbox size={15} />, label: 'Inbox', desc: 'Documents waiting for your signature', action: () => setTab('inbox') },
          { icon: <FiFileText size={15} />, label: 'Documents', desc: 'View all sent documents', action: () => setTab('contracts') },
          { icon: <FiLayout size={15} />, label: 'Templates', desc: 'Manage reusable templates', action: () => setTab('templates') },
          { icon: <FiUserPlus size={15} />, label: 'Contacts', desc: 'Manage your contacts', action: () => setTab('contacts') },
          { icon: <FiLink size={15} />, label: 'Signing Links', desc: 'Manage public signing links', action: () => setTab('signingLinks') },
          { icon: <FiBarChart2 size={15} />, label: 'Reports', desc: 'Analytics and export', action: () => setTab('reports') },
          { icon: <FiCreditCard size={15} />, label: 'Billing', desc: 'Plan, credits, and usage', action: () => setTab('billing') },
          { icon: <FiSettings size={15} />, label: 'Settings', desc: 'Account and branding settings', action: () => setTab('settings') },
          { icon: <FiMessageSquare size={15} />, label: 'Support', desc: 'Chat with support', action: () => setTab('support') },
          { icon: <FiKey size={15} />, label: 'API Keys', desc: 'Manage API keys', action: () => setTab('apiKeys') },
          { icon: <FiUsers size={15} />, label: 'Team', desc: 'Manage team members', action: () => setTab('team') },
          { icon: <FiShield size={15} />, label: 'Keyboard shortcuts', desc: 'Show all shortcuts', action: () => { setShowShortcutsModal(true); } },
        ].filter(c => {
          const q = cmdQuery.trim().toLowerCase();
          if (!q) return true;
          return c.label.toLowerCase().includes(q) || c.desc.toLowerCase().includes(q);
        });

        const runCmd = (cmd) => {
          cmd.action();
          setShowCommandPalette(false);
          setCmdQuery('');
        };

        const handlePaletteKey = (e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setCmdIdx(i => Math.min(i + 1, allCmds.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setCmdIdx(i => Math.max(i - 1, 0)); }
          else if (e.key === 'Enter') { if (allCmds[cmdIdx]) runCmd(allCmds[cmdIdx]); }
          else if (e.key === 'Escape') { setShowCommandPalette(false); }
        };

        return (
          <div className="kbd-modal-overlay" onClick={() => setShowCommandPalette(false)}>
            <div
              style={{ background: 'var(--black-bg-card-2)', border: '1px solid var(--black-border)', borderRadius: 16, width: '100%', maxWidth: 520, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Search bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--black-border)' }}>
                <FiSearch size={16} color="var(--text-on-black-muted)" style={{ flexShrink: 0 }} />
                <input
                  ref={cmdInputRef}
                  autoFocus
                  type="text"
                  placeholder="Search commands…"
                  value={cmdQuery}
                  onChange={e => { setCmdQuery(e.target.value); setCmdIdx(0); }}
                  onKeyDown={handlePaletteKey}
                  style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-on-black)', fontSize: 15, lineHeight: 1 }}
                />
                <kbd style={{ flexShrink: 0, fontSize: 11, opacity: 0.5 }}>Esc</kbd>
              </div>
              {/* Results */}
              <div style={{ maxHeight: 340, overflowY: 'auto', padding: '6px 0' }}>
                {allCmds.length === 0 ? (
                  <div style={{ padding: '20px 18px', color: 'var(--text-on-black-muted)', fontSize: 13, textAlign: 'center' }}>No commands found</div>
                ) : allCmds.map((cmd, i) => (
                  <button
                    key={cmd.label}
                    type="button"
                    onMouseEnter={() => setCmdIdx(i)}
                    onClick={() => runCmd(cmd)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 18px',
                      background: i === cmdIdx ? `${primaryColor}18` : 'transparent',
                      border: 'none', cursor: 'pointer', textAlign: 'left',
                      borderLeft: i === cmdIdx ? `3px solid ${primaryColor}` : '3px solid transparent',
                      transition: 'background 0.1s',
                    }}
                  >
                    <span style={{ color: i === cmdIdx ? primaryColor : 'var(--text-on-black-muted)', flexShrink: 0 }}>{cmd.icon}</span>
                    <span style={{ flex: 1 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-on-black)', lineHeight: 1.3 }}>{cmd.label}</span>
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--text-on-black-muted)', marginTop: 1 }}>{cmd.desc}</span>
                    </span>
                    {i === cmdIdx && <FiChevronRight size={13} color="var(--text-on-black-muted)" />}
                  </button>
                ))}
              </div>
              {/* Footer hint */}
              <div style={{ padding: '8px 18px', borderTop: '1px solid var(--black-border)', display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-on-black-muted)' }}>
                <span><kbd>↑↓</kbd> navigate</span>
                <span><kbd>↵</kbd> select</span>
                <span><kbd>Esc</kbd> close</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 199 }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`sidebar${sidebarOpen ? ' sidebar--open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            {branding.logo_url && (
              <div className="sidebar-logo-box" aria-hidden="true">
                <img
                  src={getBrandingLogoUrl(branding.logo_url)}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; refreshBranding(); }}
                />
              </div>
            )}
            {(() => {
              const name = (branding.product_name || 'SignTada').trim();
              let first, rest;
              if (name === 'SignTada') {
                first = 'Sign';
                rest = 'Tada';
              } else {
                const parts = name.split(/\s+/);
                first = parts[0] || '';
                rest = parts.slice(1).join(' ');
              }
              const adaptColor = (color) => {
                if (!color) return color;
                const c = color.toLowerCase().replace(/\s/g, '');
                if (theme === 'light' && (c === '#ffffff' || c === '#fff' || c === 'white')) return '#171717';
                if (theme === 'dark' && (c === '#000000' || c === '#000' || c === 'black')) return '#fafafa';
                return color;
              };
              const firstColor = adaptColor(branding.first_part_color || '#ffffff');
              const restColor = adaptColor(branding.rest_part_color || branding.primary_color || '#FFD600');
              return (
                <span className="sidebar-logo-text">
                  <span style={{ color: firstColor, fontWeight: 800 }}>{first}</span>
                  {rest && <span style={{ color: restColor, fontWeight: 800 }}>{rest}</span>}
                </span>
              );
            })()}
          </div>
        </div>
        <nav className="sidebar-nav">
          <div className="sidebar-section">
            <div className="sidebar-section-title">Workspace</div>
            <button className={`sidebar-link ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}><FiHome className="sidebar-link-icon" /><span>Dashboard</span></button>
            <button className={`sidebar-link ${tab === 'inbox' ? 'active' : ''}`} onClick={() => { setTab('inbox'); }} style={{ position: 'relative' }}>
              <FiInbox className="sidebar-link-icon" /><span>Inbox</span>
              {inboxUnseenCount > 0 && <span className="sidebar-badge" style={{ marginLeft: 'auto', minWidth: 20, height: 20, borderRadius: 10, background: 'var(--primary)', color: 'var(--text-on-black)', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px' }}>{inboxUnseenCount > 99 ? '99+' : inboxUnseenCount}</span>}
            </button>
            <button className={`sidebar-link ${tab === 'contracts' ? 'active' : ''}`} onClick={() => setTab('contracts')}><FiFileText className="sidebar-link-icon" /><span>Documents</span></button>
            <button className={`sidebar-link ${tab === 'reports' ? 'active' : ''}`} onClick={() => setTab('reports')}><FiBarChart2 className="sidebar-link-icon" /><span>Reports</span></button>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Create</div>
            <button className={`sidebar-link ${tab === 'initiate' ? 'active' : ''}`} onClick={() => setTab('initiate')}><FiPlusCircle className="sidebar-link-icon" /><span>New Request</span></button>
            <button className={`sidebar-link ${tab === 'batchSend' ? 'active' : ''}`} onClick={() => setTab('batchSend')}><FiUsers className="sidebar-link-icon" /><span>Batch Send</span></button>
            <button className={`sidebar-link ${tab === 'templates' ? 'active' : ''}`} onClick={() => setTab('templates')}><FiLayout className="sidebar-link-icon" /><span>Templates</span></button>
            <button className={`sidebar-link ${tab === 'contacts' ? 'active' : ''}`} onClick={() => setTab('contacts')}><FiUserPlus className="sidebar-link-icon" /><span>Contacts</span></button>
            <button className={`sidebar-link ${tab === 'signingLinks' ? 'active' : ''}`} onClick={() => setTab('signingLinks')}><FiLink className="sidebar-link-icon" /><span>Signing Links</span></button>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Integrate</div>
            <button className={`sidebar-link ${tab === 'webhooks' ? 'active' : ''}`} onClick={() => setTab('webhooks')}><WebhookIcon className="sidebar-link-icon" /><span>Webhooks</span></button>
            <button className={`sidebar-link ${tab === 'apiKeys' ? 'active' : ''}`} onClick={() => setTab('apiKeys')}><FiKey className="sidebar-link-icon" /><span>API Keys</span></button>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Account</div>
            <button className={`sidebar-link ${tab === 'team' ? 'active' : ''}`} onClick={() => setTab('team')}><FiUsers className="sidebar-link-icon" /><span>Team</span></button>
            <button className={`sidebar-link ${tab === 'billing' ? 'active' : ''}`} onClick={() => setTab('billing')}><FiCreditCard className="sidebar-link-icon" /><span>Billing</span></button>
            <button className={`sidebar-link ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}><FiSettings className="sidebar-link-icon" /><span>Settings</span></button>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Help</div>
            <button className={`sidebar-link ${tab === 'support' ? 'active' : ''}`} onClick={() => { setTab('support'); setSupportUnreadCount(0); }} style={{ position: 'relative' }}>
              <FiMessageSquare className="sidebar-link-icon" /><span>Support</span>
              {supportUnreadCount > 0 && (
                <span style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  minWidth: 18, height: 18, background: '#25d366', color: '#fff',
                  borderRadius: 9, fontSize: 10, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 5px', lineHeight: 1,
                }}>{supportUnreadCount > 99 ? '99+' : supportUnreadCount}</span>
              )}
            </button>
            <button
              type="button"
              className="sidebar-link sidebar-link-external"
              onClick={() => window.open(DOCS_URL, '_blank', 'noopener,noreferrer')}
              title="Opens in new tab"
              aria-label="Open Documentation in new tab"
            >
              <FiExternalLink className="sidebar-link-icon" /><span>Documentation</span>
            </button>
          </div>
        </nav>
        {branding.product_name !== 'SignTada' && (
          <div className="sidebar-footer-powered">
            <span className="powered-by-text">Powered by SignTada</span>
          </div>
        )}
      </aside>

      <div className="main-content">
        <header className="topbar">
          {/* Mobile hamburger */}
          <button
            className="hamburger-menu"
            onClick={() => setSidebarOpen(v => !v)}
            aria-label="Toggle menu"
            style={{ display: 'none', marginRight: 12 }}
            id="hamburger-btn"
          >
            <FiMenu />
          </button>
          <div className="topbar-title">
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-on-black)', textTransform: 'capitalize' }}>
              {tab === 'overview' ? 'Overview' : tab === 'contracts' ? 'My Documents' : tab === 'inbox' ? 'Inbox' : tab === 'templates' ? 'Templates' : tab === 'contacts' ? 'Contacts' : tab === 'reports' ? 'Reports' : tab === 'support' ? 'Support' : tab === 'webhooks' ? 'Webhooks' : tab === 'apiKeys' ? 'API Keys' : tab === 'team' ? 'Team' : tab === 'billing' ? 'Billing' : tab === 'settings' ? 'Settings' : tab === 'initiate' ? 'New Request' : tab === 'batchSend' ? 'Batch Send' : tab === 'signingLinks' ? 'Signing Links' : tab}
            </h2>
          </div>
          <div className="topbar-actions">

            {/* SUPER ADMIN DROPDOWN */}
            {isSuperAdmin && (
                <div style={{display:'flex', alignItems:'center', marginRight: 15}}>
                   <span className="topbar-viewing-label" style={{fontSize: 12, marginRight: 8}}>Viewing:</span>
                   <select className="admin-select" value={selectedAdminCompany} onChange={(e) => setSelectedAdminCompany(e.target.value)}>
                     <option value="">All Companies</option>
                     {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                   </select>
                </div>
            )}

            {/* Command palette button */}
            <button
              type="button"
              className="icon-btn-header"
              title="Search (⌘K)"
              onClick={() => { setCmdQuery(''); setCmdIdx(0); setShowCommandPalette(true); }}
              style={{ fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, background: 'var(--black-bg-card-2)', border: '1px solid var(--black-border)', color: 'var(--text-on-black-muted)', cursor: 'pointer', minWidth: 160, justifyContent: 'space-between' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiSearch size={14} /> Search...
              </span>
              <kbd style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'var(--black-bg-card-4)', border: '1px solid var(--black-border)', color: 'var(--text-on-black-muted)', fontFamily: 'inherit' }}>⌘K</kbd>
            </button>
            {/* Keyboard shortcut hint */}
            <button
              type="button"
              className="icon-btn-header"
              title="Keyboard shortcuts (?)"
              onClick={() => setShowShortcutsModal(true)}
              style={{ fontSize: 13, fontWeight: 700 }}
            >
              ?
            </button>

            {/* NOTIFICATION BELL */}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="icon-btn-header"
                title="Notifications"
                aria-label="Notifications"
                onClick={() => {
                  setShowNotifDropdown(v => !v);
                  if (!showNotifDropdown) fetchNotifications();
                }}
              >
                <FiBell />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: 2, right: 2,
                    minWidth: 16, height: 16,
                    background: '#ef4444', color: '#fff',
                    borderRadius: '50%', fontSize: 10, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 4px', pointerEvents: 'none', lineHeight: 1,
                  }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifDropdown && (
                <div style={{
                  position: 'absolute', top: 46, right: 0,
                  width: 340, maxHeight: 440,
                  background: 'var(--black-bg-card-3)', border: '1px solid var(--black-border)',
                  borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                  zIndex: 150, overflow: 'hidden',
                  animation: 'slideDown 0.2s ease',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--black-border)' }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-on-black)' }}>Notifications</span>
                    {unreadCount > 0 && (
                      <button type="button" onClick={handleMarkAllRead} style={{ fontSize: 12, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    {notifLoading ? (
                      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-on-black-muted)', fontSize: 13 }}>Loading...</div>
                    ) : notifications.length === 0 ? (
                      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-on-black-muted)', fontSize: 13 }}>
                        <FiBell size={28} style={{ display: 'block', margin: '0 auto 8px', opacity: 0.4 }} />
                        No notifications yet
                      </div>
                    ) : (
                      notifications.slice(0, 8).map(n => {
                        const typeIconMap = {
                          document_signed: <FiPenTool size={15} />,
                          document_completed: <FiCheckCircle size={15} />,
                          ticket_reply: <FiMessageSquare size={15} />,
                          ticket_created: <FiMessageSquare size={15} />,
                          credits_low: <FiCreditCard size={15} />,
                          signing_required: <FiMail size={15} />,
                        };
                        return (
                          <button
                            key={n.id}
                            type="button"
                            onClick={() => handleMarkNotifRead(n)}
                            style={{
                              width: '100%', textAlign: 'left', padding: '12px 16px',
                              border: 'none', background: n.read ? 'transparent' : 'rgba(255,214,0,0.04)',
                              cursor: 'pointer', borderBottom: '1px solid var(--black-border)',
                              display: 'flex', alignItems: 'flex-start', gap: 12,
                              transition: 'background 0.15s',
                            }}
                          >
                            <div style={{
                              width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                              background: n.read ? 'var(--black-bg-card-4)' : 'rgba(255,214,0,0.15)',
                              color: n.read ? 'var(--text-on-black-muted)' : 'var(--primary)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {typeIconMap[n.type] || <FiBell size={15} />}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: n.read ? 400 : 600, color: 'var(--text-on-black)', marginBottom: 2 }}>{n.title}</div>
                              <div style={{ fontSize: 12, color: 'var(--text-on-black-muted)', lineHeight: 1.4 }}>{n.message}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-on-black-muted)', marginTop: 4 }}>{notifTimeAgo(n.created_at)}</div>
                            </div>
                            {!n.read && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', flexShrink: 0, marginTop: 6 }} />}
                          </button>
                        );
                      })
                    )}
                  </div>
                  <div style={{ padding: '10px 16px', borderTop: '1px solid var(--black-border)', textAlign: 'center' }}>
                    <button type="button" onClick={() => setShowNotifDropdown(false)} style={{ fontSize: 12, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                      Close
                    </button>
                  </div>
                </div>
              )}
              {showNotifDropdown && <div className="dropdown-backdrop" onClick={() => setShowNotifDropdown(false)} />}
            </div>

            {/* CREDITS DISPLAY BADGE */}
            <div className="credits-badge" title="View billing" onClick={() => setTab('billing')} style={{cursor: 'pointer'}} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('billing'); } }}>
                <FiCreditCard size={15} style={{marginRight: 6, color: '#F59E0B'}}/>
                <div style={{display:'flex', flexDirection:'column', lineHeight: 1}}>
                    <span style={{fontSize: 9, fontWeight: 700, color: 'var(--text-on-black-muted)', textTransform:'uppercase'}}>Credits</span>
                    <span style={{fontSize: 13, fontWeight: 800, color: 'var(--text-on-black)'}}>{getDisplayCredits()}</span>
                </div>
            </div>

            <button type="button" className="icon-btn-header" title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} onClick={toggleTheme} data-testid="theme-toggle" aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              {theme === 'dark' ? <FiSun /> : <FiMoon />}
            </button>
            <button className="icon-btn-header" title="Settings" onClick={() => setTab('settings')}><FiSettings /></button>
            <div className="profile-menu-container">
              <button className="icon-btn-header profile-btn" onClick={() => setShowProfileMenu(!showProfileMenu)}><FiUser /></button>
              {showProfileMenu && (
                <div className="profile-dropdown">
                  <div className="profile-header"><div className="profile-avatar"><FiUser /></div><div className="profile-details"><span className="profile-email">{userEmail}</span><span className="profile-role">{userRoles[0]}</span></div></div>
                  <div className="profile-links">
                    <button onClick={() => { setTab('settings'); setShowProfileMenu(false); }}><FiSettings /> Account Settings</button>
                    <button className="text-danger" onClick={handleLogout}><FiLogOut /> Sign Out</button>
                  </div>
                </div>
              )}
            </div>
            {showProfileMenu && <div className="dropdown-backdrop" onClick={() => setShowProfileMenu(false)}></div>}
          </div>
        </header>

        <div className={`page-content${tab === 'support' ? ' page-content--no-pad' : ''}`}>
          {tab === 'overview' && (
            <>
              {/* Overview Header */}
              <div className="overview-header">
                <div>
                  <div className="overview-header-title">Welcome back{userEmail ? `, ${userEmail.split('@')[0]}` : ''} 👋</div>
                  <div className="overview-header-subtitle">Here's what's happening with your documents today.</div>
                </div>
                <div className="overview-header-actions">
                  <button type="button" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '8px 16px' }} onClick={() => setTab('initiate')}>
                    <FiPlusCircle size={15} /> New Request
                  </button>
                </div>
              </div>

              {/* Inbox alert (if any) - reserved slot */}
              {/* Low Credits Alert Banner */}
              {(() => {
                // For Super Admin, use getDisplayCredits() which handles company selection
                // For regular users, use availableCredits
                const displayCreditsValue = isSuperAdmin 
                  ? parseFloat(getDisplayCredits() || 0) 
                  : availableCredits;
                
                // Show alert when credits are low
                // For Super Admin: check the displayed credits (sum if "All Companies", or specific company if selected)
                // For regular users: check their own credits
                return displayCreditsValue < 2.0 && (
                  <div style={{
                    background: '#FEF3C7',
                    border: '1px solid #F59E0B',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: '#92400E'
                  }}>
                    <FiCreditCard size={18} />
                    <div>
                      <strong>Low Credits:</strong> {displayCreditsValue.toFixed(1)} remaining. Contact admin to add credits.
                    </div>
                  </div>
                );
              })()}
              
              <section className="dashboard-grid dashboard-grid--six">
                {(() => {
                  const trend = (curr, prev) => {
                    if (prev == null || prev === undefined || curr == null || curr === undefined) return null;
                    const prevNum = Number(prev);
                    if (prevNum === 0) return null;
                    const currNum = Number(curr);
                    const pct = ((currNum - prevNum) / prevNum) * 100;
                    const increased = currNum > prevNum;
                    return { pct, increased };
                  };
                  const parsePct = (v) => {
                    const s = String(v || '0').replace(/%/g, '');
                    return parseFloat(s) || 0;
                  };
                  const Trend = ({ t }) => t ? (
                    <span style={{ fontSize: 12, fontWeight: 600, marginLeft: 6, color: t.increased ? '#10B981' : '#EF4444' }} title="vs previous period">
                      {t.increased ? '▲' : '▼'} {t.pct >= 0 ? '+' : ''}{Number(t.pct.toFixed(1))}%
                    </span>
                  ) : null;
                  const primary = branding.primary_color || '#FFD600';
                  return (
                    <>
                      <div className="stats-card">
                        <div className="stats-card-header">
                          <div className="stats-card-icon" style={{ background: `${primary}20`, color: primary }}><FiFileText size={16} /></div>
                          <div className="stats-title">Total Documents</div>
                        </div>
                        <div className="stats-value" style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>{stats.total}<Trend t={trend(stats.total, statsPrevious?.total)} /></div>
                        <div className="stats-card-bar" style={{ '--bar-color': primary, '--bar-pct': '100%' }} />
                      </div>
                      <div className="stats-card stats-card--signed">
                        <div className="stats-card-header">
                          <div className="stats-card-icon" style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}><FiCheckCircle size={16} /></div>
                          <div className="stats-title">Signed</div>
                        </div>
                        <div className="stats-value" style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>{stats.signed}<Trend t={trend(stats.signed, statsPrevious?.signed)} /></div>
                        <div className="stats-card-bar" style={{ '--bar-color': '#10B981', '--bar-pct': stats.total > 0 ? `${Math.min(100,(stats.signed/stats.total)*100).toFixed(0)}%` : '0%' }} />
                      </div>
                      <div className="stats-card stats-card--pending">
                        <div className="stats-card-header">
                          <div className="stats-card-icon" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}><FiClock size={16} /></div>
                          <div className="stats-title">Pending</div>
                        </div>
                        <div className="stats-value" style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>{stats.pending}<Trend t={trend(stats.pending, statsPrevious?.pending)} /></div>
                        <div className="stats-card-bar" style={{ '--bar-color': '#F59E0B', '--bar-pct': stats.total > 0 ? `${Math.min(100,(stats.pending/stats.total)*100).toFixed(0)}%` : '0%' }} />
                      </div>
                      <div className="stats-card stats-card--rejected">
                        <div className="stats-card-header">
                          <div className="stats-card-icon" style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}><FiXCircle size={16} /></div>
                          <div className="stats-title">Rejected</div>
                        </div>
                        <div className="stats-value" style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>{stats.rejected ?? 0}<Trend t={trend(stats.rejected ?? 0, statsPrevious?.rejected)} /></div>
                        <div className="stats-card-bar" style={{ '--bar-color': '#EF4444', '--bar-pct': stats.total > 0 ? `${Math.min(100,((stats.rejected??0)/stats.total)*100).toFixed(0)}%` : '0%' }} />
                      </div>
                      <div className="stats-card">
                        <div className="stats-card-header">
                          <div className="stats-card-icon" style={{ background: `${primary}20`, color: primary }}><FiBarChart2 size={16} /></div>
                          <div className="stats-title">Completion Rate</div>
                        </div>
                        <div className="stats-value" style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>{stats.completion_rate || stats.completionRate}<Trend t={trend(parsePct(stats.completion_rate || stats.completionRate), statsPrevious ? parsePct(statsPrevious.completion_rate || statsPrevious.completionRate) : null)} /></div>
                        <div className="stats-card-bar" style={{ '--bar-color': primary, '--bar-pct': `${Math.min(100, parsePct(stats.completion_rate || stats.completionRate)).toFixed(0)}%` }} />
                      </div>
                      <div className="stats-card stats-card--avg-time">
                        <div className="stats-card-header">
                          <div className="stats-card-icon" style={{ background: 'rgba(139,92,246,0.15)', color: '#8B5CF6' }}><FiClock size={16} /></div>
                          <div className="stats-title">Avg. Time to Sign</div>
                        </div>
                        <div className="stats-value" style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
                          {reportsData.avg_time_to_sign_hours != null ? `${Number(reportsData.avg_time_to_sign_hours).toFixed(1)} hrs` : '— hrs'}
                          <Trend t={trend(reportsData.avg_time_to_sign_hours, prevAvgTimeToSignHours)} />
                        </div>
                        <div className="stats-card-bar" style={{ '--bar-color': '#8B5CF6', '--bar-pct': '60%' }} />
                      </div>
                    </>
                  );
                })()}
              </section>

              {/* Credits Health */}
              {(() => {
                const total = (billingInfo.credits ?? 0) + (billingInfo.usage_this_month ?? 0);
                const used = Number(billingInfo.usage_this_month ?? 0);
                const remaining = Number(billingInfo.credits ?? 0);
                const pctUsed = total > 0 ? (used / total) * 100 : 0;
                const pctRemaining = total > 0 ? 100 - pctUsed : 100;
                let healthClass = 'good';
                if (pctRemaining < 20) healthClass = 'danger';
                else if (pctRemaining < 50) healthClass = 'warn';
                const now = new Date();
                const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                const daysUntilReset = Math.ceil((nextMonth - now) / (1000 * 60 * 60 * 24));
                return (
                  <div className="credits-health-card" style={{ marginBottom: '24px' }}>
                    <div className="credits-health-card-header">
                      <div className="credits-health-card-title">
                        <FiCreditCard size={15} />
                        Credits Health
                      </div>
                      <div className="credits-health-reset-badge">
                        <FiRefreshCcw size={10} />
                        Resets in {daysUntilReset} day{daysUntilReset !== 1 ? 's' : ''}
                      </div>
                    </div>
                    {billingLoading ? (
                      <SigningLoader size="sm" label="Loading..." />
                    ) : (
                      <>
                        <div className="credits-health-stats-row">
                          <div className="credits-health-stat">
                            <span className="credits-health-stat-label">Available</span>
                            <span className={`credits-health-stat-value ${healthClass}`}>{remaining.toFixed(1)}</span>
                          </div>
                          <div className="credits-health-stat">
                            <span className="credits-health-stat-label">Used This Month</span>
                            <span className="credits-health-stat-value">{used.toFixed(1)}</span>
                          </div>
                          <div className="credits-health-stat">
                            <span className="credits-health-stat-label">Total Allocated</span>
                            <span className="credits-health-stat-value">{total.toFixed(1)}</span>
                          </div>
                        </div>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 11, color: 'var(--text-on-black-muted)', fontWeight: 600 }}>
                            <span>{pctUsed.toFixed(0)}% used</span>
                            <span>{pctRemaining.toFixed(0)}% remaining</span>
                          </div>
                          <div className="credits-health-progress-track">
                            <div className={`credits-health-progress-fill-v2 ${healthClass}`} style={{ width: `${pctUsed}%` }} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
              
              {/* Time Range Selector */}
              <div style={{ marginTop: '20px', marginBottom: '10px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button 
                  className={`tab-btn ${timeRange === 'ALL_TIME' ? 'active' : ''}`}
                  onClick={() => setTimeRange('ALL_TIME')}
                  style={{ fontSize: '13px', padding: '6px 14px' }}
                >
                  All Time
                </button>
                <button 
                  className={`tab-btn ${timeRange === '7_DAYS' ? 'active' : ''}`}
                  onClick={() => setTimeRange('7_DAYS')}
                  style={{ fontSize: '13px', padding: '6px 14px' }}
                >
                  Last 7 Days
                </button>
                <button 
                  className={`tab-btn ${timeRange === '30_DAYS' ? 'active' : ''}`}
                  onClick={() => setTimeRange('30_DAYS')}
                  style={{ fontSize: '13px', padding: '6px 14px' }}
                >
                  Last 30 Days
                </button>
                <button 
                  className={`tab-btn ${timeRange === '90_DAYS' ? 'active' : ''}`}
                  onClick={() => setTimeRange('90_DAYS')}
                  style={{ fontSize: '13px', padding: '6px 14px' }}
                >
                  Last 90 Days
                </button>
              </div>
              
              <section className="charts-section">
                {/* Chart 1: Signing Activity – Area chart (daily trend) */}
                <div className="chart-card">
                  <div className="chart-card-header">
                    <div>
                      <h3 style={{ margin: 0 }}>Signing Timeliness</h3>
                      <span className="chart-card-subtitle">Daily signing velocity</span>
                    </div>
                    <span className="chart-badge">Trend</span>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={(reportsData.signing_velocity || []).slice(-30)} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                      <defs>
                        <linearGradient id="signingGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={branding.primary_color || '#FFD600'} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={branding.primary_color || '#FFD600'} stopOpacity={0.03} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--black-border)" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-on-black-muted)' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: 'var(--text-on-black-muted)' }} tickLine={false} axisLine={false} allowDecimals={false} />
                      <ReTooltip formatter={(v) => [`${v} docs`, 'Signed']} contentStyle={{ background: 'var(--black-bg-card-4)', border: '1px solid var(--black-border)', borderRadius: 8 }} labelStyle={{ color: 'var(--text-on-black)' }} itemStyle={{ color: 'var(--text-on-black-secondary)' }} />
                      <Area type="monotone" dataKey="count" stroke={branding.primary_color || '#FFD600'} strokeWidth={2.5} fill="url(#signingGrad)" dot={false} activeDot={{ r: 4, fill: branding.primary_color || '#FFD600' }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Chart 2: Document Status Distribution – Donut chart (unique data & type) */}
                <div className="chart-card">
                  <div className="chart-card-header">
                    <div>
                      <h3 style={{ margin: 0 }}>Signing Timeline</h3>
                      <span className="chart-card-subtitle">Status distribution</span>
                    </div>
                    <span className="chart-badge">Donut</span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Signed', value: stats.signed || 0 },
                            { name: 'Pending', value: stats.pending || 0 },
                            { name: 'Rejected', value: stats.rejected || 0 },
                          ].filter(d => d.value > 0)}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={85}
                          paddingAngle={3}
                          strokeWidth={0}
                        >
                          <Cell fill="#10B981" />
                          <Cell fill="#F59E0B" />
                          <Cell fill="#EF4444" />
                        </Pie>
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: 'var(--text-on-black-muted)' }} />
                        <ReTooltip contentStyle={{ background: 'var(--black-bg-card-4)', border: '1px solid var(--black-border)', borderRadius: 8 }} itemStyle={{ color: 'var(--text-on-black-secondary)' }} />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Center label */}
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -58%)', textAlign: 'center', pointerEvents: 'none' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-on-black)', lineHeight: 1 }}>{stats.total || 0}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-on-black-muted)', marginTop: 2 }}>total</div>
                    </div>
                  </div>
                </div>

                {/* Chart 3: Monthly Volume – Grouped Bar chart */}
                <div className="chart-card">
                  <div className="chart-card-header">
                    <div>
                      <h3 style={{ margin: 0 }}>Monthly Document Volume</h3>
                      <span className="chart-card-subtitle">Docs created vs credits used</span>
                    </div>
                    <span className="chart-badge">6 months</span>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={(reportsData.monthly_volume || []).slice(-6)} margin={{ top: 8, right: 8, left: -16, bottom: 0 }} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--black-border)" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--text-on-black-muted)' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: 'var(--text-on-black-muted)' }} tickLine={false} axisLine={false} allowDecimals={false} />
                      <ReTooltip
                        cursor={{ fill: 'rgba(255, 214, 0, 0.10)' }}
                        contentStyle={{ background: 'var(--black-bg-card-2)', border: '1px solid var(--black-border-light)', borderRadius: 8 }}
                        labelStyle={{ color: 'var(--text-on-black)' }}
                        itemStyle={{ color: 'var(--text-on-black-secondary)' }}
                      />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: 'var(--text-on-black-muted)' }} />
                      <Bar dataKey="documents_created" name="Docs Created" fill={branding.primary_color || '#FFD600'} radius={[4, 4, 0, 0]} maxBarSize={28} />
                      <Bar dataKey="credits_used" name="Credits Used" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>

              {/* Recent Documents and Recent Activity - Side by Side */}
              <section style={{ marginTop: '28px', display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '20px' }}>
                {/* Recent Documents Table */}
                <div className="recent-docs-table-wrap">
                  <div className="recent-docs-table-header">
                    <div className="recent-docs-table-title">
                      <div className="recent-docs-table-title-icon"><FiFileText size={14} /></div>
                      Recent Documents
                    </div>
                    <button type="button" className="recent-docs-view-all" onClick={() => setTab('contracts')}>
                      View All <FiChevronRight size={12} />
                    </button>
                  </div>
                  {filteredDocs.length === 0 ? (
                    <div className="empty-state-card" style={{ margin: '20px auto', border: 'none', background: 'transparent' }}>
                      <div className="empty-state-icon"><FiFileText size={28} /></div>
                      <div className="empty-state-title">No documents yet</div>
                      <div className="empty-state-desc">Send your first document for signature.</div>
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => setTab('initiate')}>New Request</button>
                    </div>
                  ) : (
                    <table className="recent-docs-table">
                      <thead>
                        <tr>
                          <th>Document ID</th>
                          <th>Name</th>
                          <th>Recipients</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDocs.slice(0, 6).map((doc) => {
                          const refId = doc.reference_id || doc.referenceId || 'N/A';
                          const recipients = doc.recipients || [];
                          const signedRecipients = recipients.filter(r => getRecipientStatusValue(r) === 'Signed').length;
                          const rejectedRecipients = recipients.filter(r => getRecipientStatusValue(r) === 'Rejected').length;
                          const totalRecipients = recipients.length;
                          const isFullySigned = doc.status === 'Signed' || (totalRecipients > 0 && signedRecipients === totalRecipients);
                          const hasRejected = rejectedRecipients > 0;
                          let statusClass = isFullySigned ? 'signed' : 'pending';
                          let statusText = 'Pending';
                          if (hasRejected && !isFullySigned) {
                            statusClass = 'rejected';
                            statusText = 'Rejected';
                          } else if (isFullySigned) {
                            statusText = 'Completed';
                          } else if (totalRecipients > 0 && signedRecipients > 0) {
                            statusText = `${signedRecipients}/${totalRecipients} Signed`;
                          }
                          const docDate = doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                          const isExpOv = expandedDocId === refId;
                          return (
                            <React.Fragment key={refId}>
                              <tr style={{ cursor: 'pointer' }} className={isExpOv ? 'doc-row-expanded' : ''} onClick={() => toggleRow(refId)}>
                                <td><span className="doc-id-cell">{String(refId).slice(0, 10)}{String(refId).length > 10 ? '…' : ''}</span></td>
                                <td><span className="doc-name-cell" title={doc.document_name || 'Document'}>{doc.document_name || 'Document'}</span></td>
                                <td>
                                  <div className="recipients-cell">
                                    <div className="recipient-avatars">
                                      {recipients.slice(0, 3).map((r, ri) => (
                                        <div key={r.email || ri} className="recipient-avatar-mini" title={r.email}>
                                          {(r.email || r.name || '?')[0].toUpperCase()}
                                        </div>
                                      ))}
                                    </div>
                                    {totalRecipients > 3 && <span>+{totalRecipients - 3}</span>}
                                    {totalRecipients === 0 && <span>—</span>}
                                  </div>
                                </td>
                                <td><span className={`status-badge ${statusClass}`} style={{ fontSize: 10, padding: '3px 8px', minWidth: 'unset' }}>{statusText}</span></td>
                                <td><span className="date-cell">{docDate}</span></td>
                                <td onClick={e => e.stopPropagation()}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ fontSize: 11, color: 'var(--text-on-black-muted)' }}>{isExpOv ? 'Collapse' : 'Details'}</span>
                                    <FiChevronDown size={13} style={{ color: 'var(--text-on-black-muted)', transition: 'transform 0.2s', transform: isExpOv ? 'rotate(180deg)' : 'rotate(0)' }} />
                                  </div>
                                </td>
                              </tr>
                              {isExpOv && (
                                <tr className="doc-detail-row">
                                  <td colSpan={6} style={{ padding: 0, border: 'none' }}>
                                    <div className="doc-detail-panel">
                                      <div className="doc-detail-section">
                                        <div className="doc-detail-label">Recipients</div>
                                        {recipients.length === 0 ? <span style={{ fontSize: 13, color: 'var(--text-on-black-muted)' }}>—</span> : (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {recipients.map((r, ri) => {
                                              const recipientBadge = getRecipientBadgeMeta(r);
                                              return (
                                              <div key={r.email || ri} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div className="recipient-avatar-mini" style={{ flexShrink: 0 }}>{(r.email || r.name || '?')[0].toUpperCase()}</div>
                                                <div>
                                                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-on-black)' }}>{r.name || r.email}</div>
                                                  {r.name && <div style={{ fontSize: 11, color: 'var(--text-on-black-muted)' }}>{r.email}</div>}
                                                </div>
                                                <span className={`status-badge ${recipientBadge.className}`} style={{ fontSize: 10, padding: '2px 6px', marginLeft: 'auto' }}>
                                                  {recipientBadge.label}
                                                </span>
                                              </div>
                                            )})}
                                          </div>
                                        )}
                                      </div>
                                      <div className="doc-detail-actions">
                                        <button type="button" className="btn btn-primary" style={{ fontSize: 13, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => navigate(`/documents/${refId}`)}>
                                          <FiEye size={13} /> View Document
                                        </button>
                                        {getDownloadLink(doc) && (
                                          <a href={getDownloadLink(doc)} className="btn btn-secondary" style={{ fontSize: 13, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }} target="_blank" rel="noreferrer">
                                            <FiDownload size={13} /> Download
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Recent Activity Feed - Timeline */}
                <div className="activity-feed-wrap">
                  <div className="activity-feed-header">
                    <div className="activity-feed-title">
                      <div className="activity-feed-title-icon"><FiActivity size={14} /></div>
                      Recent Activity
                    </div>
                  </div>
                  <div className="activity-feed-body">
                    {recentActivity.length === 0 ? (
                      <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                        <FiActivity size={28} style={{ color: 'var(--text-on-black-muted)', marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                        <div style={{ fontSize: 13, color: 'var(--text-on-black-muted)' }}>No recent activity</div>
                        <div style={{ fontSize: 11, color: 'var(--text-on-black-muted)', marginTop: 4 }}>Signing and send events will show here.</div>
                      </div>
                    ) : (
                      recentActivity.map((activity, idx) => {
                        const iconColor = activity.icon_color || '#3B82F6';
                        const bgColor = `${iconColor}18`;
                        let icon = <FiActivity size={14} />;
                        if (activity.icon === 'check') icon = <FiCheckCircle size={14} />;
                        else if (activity.icon === 'send') icon = <FiSend size={14} />;
                        else if (activity.icon === 'clock') icon = <FiClock size={14} />;
                        else if (activity.icon === 'x') icon = <FiX size={14} />;
                        return (
                          <div key={activity.reference_id || activity.title || idx} className="activity-timeline-item">
                            <div className="activity-timeline-left">
                              <div className="activity-timeline-dot" style={{ background: bgColor, color: iconColor }}>
                                {icon}
                              </div>
                              {idx < recentActivity.length - 1 && <div className="activity-timeline-line" />}
                            </div>
                            <div className="activity-timeline-content">
                              <div className="activity-timeline-desc">{activity.description}</div>
                              <div className="activity-timeline-meta">
                                <span>{activity.actor}</span>
                                {activity.reference_id && <span className="activity-timeline-ref">{activity.reference_id}</span>}
                              </div>
                            </div>
                            <div className="activity-timeline-time">{activity.time_ago || 'Recently'}</div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </section>

            </>
          )}

          {tab === 'templates' && (
              <section className="table-card templates-card-wrapper">
                  <div className="table-header-row">
                      <div>
                        <h3>Document Templates</h3>
                        {templates.length > 0 && (
                          <p className="templates-subtitle">
                            {templates.length} template{templates.length > 1 ? 's' : ''} available
                          </p>
                        )}
                      </div>
                      <button className="btn btn-primary" onClick={() => navigate('/create-template')}>
                          <FiPlusCircle style={{marginRight:5}} /> Create Template
                      </button>
                  </div>
                  {templates.length === 0 ? (
                    <div className="empty-state-card">
                      <div className="empty-state-icon"><FiLayout size={32} /></div>
                      <div className="empty-state-title">No templates yet</div>
                      <div className="empty-state-desc">Templates save time by pre-placing signature fields. Create one once, send it to anyone in seconds.</div>
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/create-template')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FiPlusCircle size={13} /> Create Template</button>
                    </div>
                  ) : (
                    <div className="templates-grid">
                      {templates.map((t) => {
                        const createdDate = t.created_at ? new Date(t.created_at).toLocaleDateString() : '-';
                        // Use email if available, otherwise fallback to created_by (which should now be email from backend)
                        const createdBy = t.created_by_email || t.created_by || userEmail || '—';
                        const rolesCount = Array.isArray(t.roles) ? t.roles.length : 0;
                        const fieldCount = Array.isArray(t.text_fields) ? t.text_fields.length : 0;
                        return (
                          <article key={t._id} className="template-card">
                            <header className="template-card-header">
                              <div className="template-badge-icon">
                                <FiFileText />
                              </div>
                              <div className="template-title-group">
                                <h4 className="template-title">{t.title}</h4>
                                <span className="template-meta-pill">Template</span>
                              </div>
                              <div className="template-menu">
                                <button
                                  type="button"
                                  className="template-menu-trigger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenTemplateMenuId(
                                      openTemplateMenuId === t._id ? null : t._id
                                    );
                                  }}
                                >
                                  <FiMoreVertical />
                                </button>
                                {openTemplateMenuId === t._id && (
                                  <div
                                    className="template-menu-dropdown"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      className="template-menu-item"
                                      onClick={() => {
                                        setOpenTemplateMenuId(null);
                                        navigate(`/create-template?mode=view&id=${t._id}`);
                                      }}
                                    >
                                      <FiEye />
                                      <span>View</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="template-menu-item"
                                      onClick={() => {
                                        setOpenTemplateMenuId(null);
                                        navigate(`/create-template?mode=edit&id=${t._id}`);
                                      }}
                                    >
                                      <FiEdit2 />
                                      <span>Edit</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="template-menu-item danger"
                                      onClick={() => {
                                        setOpenTemplateMenuId(null);
                                        setDeleteConfirmModal({ open: true, templateId: t._id, templateName: t.title });
                                      }}
                                    >
                                      <FiTrash />
                                      <span>Delete</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </header>

                            <div className="template-card-body">
                              <div className="template-meta-row">
                                <span className="meta-label">Created</span>
                                <span className="meta-value">{createdDate}</span>
                              </div>
                              <div className="template-meta-row">
                                <span className="meta-label">Created By</span>
                                <span className="meta-value">{createdBy}</span>
                              </div>
                              <div className="template-meta-row">
                                <span className="meta-label">Structure</span>
                                <span className="meta-value">
                                  {rolesCount} role{rolesCount === 1 ? '' : 's'} · {fieldCount} field{fieldCount === 1 ? '' : 's'}
                                </span>
                              </div>
                            </div>

                            <footer className="template-card-footer">
                              <button
                                type="button"
                                className="btn-template-use"
                                onClick={() => {
                                  setOpenTemplateMenuId(null);
                                  setInitialTemplateIdForInitiate(t._id);
                                  setTab('initiate');
                                }}
                              >
                                Use Template
                              </button>
                            </footer>
                          </article>
                        );
                      })}
                    </div>
                  )}
              </section>
          )}

          {/* Delete Confirmation Modal */}
          {deleteConfirmModal.open && (
            <div className="delete-modal-backdrop" onClick={() => setDeleteConfirmModal({ open: false, templateId: null, templateName: "" })}>
              <div className="delete-modal" onClick={e => e.stopPropagation()}>
                <h3><FiTrash style={{ marginRight: 8, verticalAlign: 'middle' }} /> Delete Template</h3>
                <p>
                  Are you sure you want to delete the template <span className="delete-modal-template-name">"{deleteConfirmModal.templateName}"</span>? This action cannot be undone.
                </p>
                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="btn-delete-cancel"
                    onClick={() => setDeleteConfirmModal({ open: false, templateId: null, templateName: "" })}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-delete-confirm"
                    onClick={() => handleDeleteTemplate(deleteConfirmModal.templateId)}
                  >
                    <FiTrash style={{ marginRight: 6, verticalAlign: 'middle' }} /> Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === 'webhooks' && (
            <div className="webhooks-tab-wrapper">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0 }}>Webhook Integration</h2>
                {(!isSuperAdmin || selectedAdminCompany) && !isEditingWebhook && (
                    <button className="btn-edit-webhook" onClick={() => setIsEditingWebhook(true)}><FiEdit2 /> Edit Configuration</button>
                )}
              </div>

              {/* SUPER ADMIN PROMPT: If no company selected, show prompt instead of form */}
              {isSuperAdmin && !selectedAdminCompany ? (
                  <div className="card" style={{textAlign:'center', padding: 40, color: 'var(--text-on-black-muted)'}}>
                      <FiFilter size={40} style={{marginBottom: 10, color: branding.primary_color || '#FFD600'}}/>
                      <h3>Select a Company</h3>
                      <p>Please select a specific company from the dropdown menu in the top right to view and configure their webhooks.</p>
                  </div>
              ) : (
                <>
                  <div className="card elevated" style={{ marginBottom: 30 }}>
                    <h3>Current Setup</h3>
                    {webhookSaveMsg && <div className="alert-success">{webhookSaveMsg}</div>}

                    <form onSubmit={handleSaveWebhook} className="settings-form">
                      <div className="form-group">
                        <label>Webhook URL</label>
                        {isEditingWebhook ? (
                          <input type="url" required placeholder="https://your-api.com/webhook" value={webhookConfig.url} onChange={e => setWebhookConfig({ ...webhookConfig, url: e.target.value })} />
                        ) : (
                          <div className="webhook-readonly-box">{webhookConfig.url || "Not configured"}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Custom Headers (JSON format)</label>
                        {isEditingWebhook ? (
                          <textarea
                            style={{ fontFamily: 'monospace', fontSize: 12, minHeight: 100 }}
                            placeholder='{ "Authorization": "Bearer your-secret-key" }'
                            value={webhookConfig.headers}
                            onChange={e => setWebhookConfig({ ...webhookConfig, headers: e.target.value })}
                          />
                        ) : (
                          <div className="webhook-readonly-box" style={{ whiteSpace: 'pre-wrap' }}>{webhookConfig.headers === "{}" ? "No headers configured" : webhookConfig.headers}</div>
                        )}
                      </div>

                      <div className="switch-container" style={{ marginBottom: 20, border: 'none', paddingLeft: 0 }}>
                        <label className="switch">
                          <input type="checkbox" disabled={!isEditingWebhook} checked={webhookConfig.is_active} onChange={e => setWebhookConfig({ ...webhookConfig, is_active: e.target.checked })} />
                          <span className="slider"></span>
                        </label>
                        <span className="config-label">Webhook Status: {webhookConfig.is_active ? "Enabled" : "Disabled"}</span>
                      </div>

                      {isEditingWebhook && (
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button type="submit" className="btn btn-primary"><FiCheck style={{ marginRight: 5 }} /> Save Webhook</button>
                          <button type="button" className="btn btn-secondary" onClick={() => setIsEditingWebhook(false)}><FiX style={{ marginRight: 5 }} /> Cancel</button>
                        </div>
                      )}
                    </form>
                  </div>

                  <h3>Recent Activity</h3>
                  <div className="webhook-split-container">
                    <div className={`webhook-logs-left ${selectedWebhookLog ? 'shrunk' : ''}`}>
                      <div className="table-card" style={{ boxShadow: 'none' }}>
                        {webhookLogs.length === 0 ? (
                          <div className="empty-state-card">
                            <div className="empty-state-icon"><FiCode size={32} /></div>
                            <div className="empty-state-title">No webhook logs yet</div>
                            <div className="empty-state-desc">Events will appear here after you save a webhook URL and activity occurs.</div>
                          </div>
                        ) : (
                          <table className="docs-table">
                            <thead>
                              <tr>
                                <th>Ref ID</th>
                                {!selectedWebhookLog && <th>Event</th>}
                                <th>API Status</th>
                                {!selectedWebhookLog && <th>Time</th>}
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {webhookLogs.map(log => {
                                const refId = log.payload?.data?.reference_id || "N/A";
                                const isSuccess = log.response_status >= 200 && log.response_status < 300;
                                return (
                                  <tr key={log._id} className={`log-row ${selectedWebhookLog?._id === log._id ? 'active' : ''}`} onClick={() => setSelectedWebhookLog(log)}>
                                    <td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{refId}</td>
                                    {!selectedWebhookLog && <td><span className="badge badge-info" style={{ fontSize: 10 }}>{log.event_type}</span></td>}
                                    <td><span className={`webhook-status-pill ${isSuccess ? 'success' : 'error'}`}>{log.response_status}</span></td>
                                    {!selectedWebhookLog && <td style={{ fontSize: 12 }}>{new Date(log.timestamp).toLocaleTimeString()}</td>}
                                    <td><FiChevronRight color={selectedWebhookLog?._id === log._id ? (branding.primary_color || '#FFD600') : '#cbd5e1'} /></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>

                    {selectedWebhookLog && (
                      <div className="webhook-detail-middle">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}><FiCode color={branding.primary_color || '#FFD600'} /> Request Details</h3>
                          <button onClick={() => setSelectedWebhookLog(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-on-black-muted)' }}><FiX size={22} /></button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 20 }}>
                          <div className="webhook-meta-item">
                            <div className="webhook-meta-label">Reference ID</div>
                            <div className="webhook-meta-value" style={{ fontFamily: 'monospace' }}>{selectedWebhookLog.payload?.data?.reference_id || "N/A"}</div>
                          </div>
                          <div className="webhook-meta-item">
                            <div className="webhook-meta-label">Signer/Recipient</div>
                            <div className="webhook-meta-value">{selectedWebhookLog.payload?.data?.signer_name || selectedWebhookLog.payload?.data?.recipients?.[0]?.name || "N/A"}</div>
                          </div>
                          <div className="webhook-meta-item">
                            <div className="webhook-meta-label">Event Type</div>
                            <div className="webhook-meta-value"><span className="badge badge-info">{selectedWebhookLog.event_type}</span></div>
                          </div>
                          <div className="webhook-meta-item">
                            <div className="webhook-meta-label">Actions</div>
                            <button className="btn-resend-webhook" disabled={resendingId === selectedWebhookLog._id} onClick={() => handleResendWebhook(selectedWebhookLog._id)}>
                              {resendingId === selectedWebhookLog._id ? <SigningLoader size="sm" label="" /> : <FiSend />} Resend Payload
                            </button>
                          </div>
                        </div>

                        <div className="webhook-meta-label" style={{ marginBottom: 8 }}>Raw JSON Payload</div>
                        <pre className="json-pre">
                          {JSON.stringify(selectedWebhookLog.payload, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === 'inbox' && (
            <section className="table-card">
              <div className="table-header-row">
                <h3>Inbox — Pending your signature</h3>
              </div>
              {inboxDocs.length === 0 ? (
                <div className="empty-state-card">
                  <div className="empty-state-icon"><FiInbox size={32} /></div>
                  <div className="empty-state-title">You're all caught up!</div>
                  <div className="empty-state-desc">No documents are waiting for your signature right now. Check back later or explore your sent documents.</div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setTab('contracts')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FiFileText size={13} /> View Documents</button>
                </div>
              ) : (
                <>
                  <table className="docs-table">
                    <thead>
                      <tr>
                        <th>Reference ID</th>
                        <th>Document Name</th>
                        <th>Created Date</th>
                        <th style={{ textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody className="animated-tbody">
                      {inboxDocs.map((doc) => {
                        const refId = doc.reference_id || doc.referenceId || 'N/A';
                        const myRecipient = (doc.recipients || []).find(
                          (r) => (r.email || '').toLowerCase() === (userEmail || '').toLowerCase() && r.status === 'Pending'
                        );
                        const signUrl = myRecipient?.sign_link
                          ? `${window.location.origin}/sign/${refId}?token=${myRecipient.sign_link}`
                          : null;
                        return (
                          <tr
                            key={refId}
                            className="table-row-main docs-table-clickable"
                            onClick={() => signUrl && window.open(signUrl, '_blank')}
                          >
                            <td style={{ fontWeight: 600 }}>{refId}</td>
                            <td>{doc.document_name || 'Document'}</td>
                            <td>{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}</td>
                            <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                              {signUrl && (
                                <a href={signUrl} className="btn-action btn-sign" target="_blank" rel="noreferrer"><FiPenTool /> Sign Now</a>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="pagination">
                    <button disabled={inboxPage === 1} onClick={() => { setInboxPage((p) => p - 1); loadInbox(inboxPage - 1); }}>&lt; Prev</button>
                    <span>Page {inboxPage} of {inboxTotalPages || 1}</span>
                    <button disabled={inboxPage >= inboxTotalPages} onClick={() => { setInboxPage((p) => p + 1); loadInbox(inboxPage + 1); }}>Next &gt;</button>
                  </div>
                </>
              )}
            </section>
          )}

          {tab === 'contacts' && (
            <section className="table-card">
              <div className="table-header-row">
                <h3>Contacts</h3>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ position: 'relative' }}>
                    <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-on-black-muted)', pointerEvents: 'none' }} />
                    <input
                      type="text"
                      placeholder="Search contacts..."
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      style={{ paddingLeft: 30, paddingRight: 10, height: 34, fontSize: 13, background: 'var(--black-bg-card-3)', border: '1px solid var(--black-border)', borderRadius: 6, color: 'var(--text-on-black)', width: 200 }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => openContactModal(null)}
                    disabled={isSuperAdmin && !selectedAdminCompany}
                  >
                    <FiUserPlus style={{ marginRight: 6 }} /> Add Contact
                  </button>
                </div>
              </div>
              {isSuperAdmin && !selectedAdminCompany ? (
                <p style={{ color: 'var(--text-on-black-muted)', fontStyle: 'italic', padding: 20 }}>Select a company from the dropdown above to view or manage contacts.</p>
              ) : contactsList.length === 0 ? (
                <div className="empty-state-card">
                  <div className="empty-state-icon"><FiUserPlus size={32} /></div>
                  <div className="empty-state-title">No contacts yet</div>
                  <div className="empty-state-desc">Add contacts to quickly use them as recipients when creating a new request.</div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => openContactModal(null)}><FiUserPlus style={{ marginRight: 6 }} /> Add Contact</button>
                </div>
              ) : (
                <table className="docs-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="animated-tbody">
                    {contactsList.filter(c => {
                      if (!contactSearch.trim()) return true;
                      const q = contactSearch.toLowerCase();
                      return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
                    }).map((c) => (
                      <tr key={c._id}>
                        <td style={{ fontWeight: 600 }}>{c.name}</td>
                        <td>{c.email}</td>
                        <td>{c.phone || '—'}</td>
                        <td className="actions-cell">
                          <div className="action-btn-group">
                            <button type="button" className="btn-action btn-audit" onClick={() => openContactModal(c)}><FiEdit2 /> Edit</button>
                            <button type="button" className="btn-action btn-download" style={{ color: '#ef4444' }} onClick={() => handleContactDelete(c._id)}><FiTrash /> Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {contactsList.length > 0 && contactSearch.trim() && !contactsList.some(c => (c.name || '').toLowerCase().includes(contactSearch.toLowerCase()) || (c.email || '').toLowerCase().includes(contactSearch.toLowerCase())) && (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-on-black-muted)', padding: 20 }}>No contacts match "{contactSearch}"</td></tr>
                    )}
                  </tbody>
                </table>
              )}
              {showContactModal && (
                <div className="modal-backdrop" onClick={() => setShowContactModal(false)}>
                  <div className="modal-content" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <h3 style={{ margin: 0 }}>{editingContactId ? 'Edit Contact' : 'Add Contact'}</h3>
                      <button type="button" className="icon-btn-header" onClick={() => setShowContactModal(false)}><FiX /></button>
                    </div>
                    <form onSubmit={handleContactSubmit}>
                      <div style={{ marginBottom: 12 }}>
                        <label className="settings-field-label" style={{ display: 'block', marginBottom: 4 }}>Name</label>
                        <input type="text" required value={contactForm.name} onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))} placeholder="John Doe" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--black-border-light)', background: 'var(--black-bg-card-4)', color: 'var(--text-on-black)' }} />
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <label className="settings-field-label" style={{ display: 'block', marginBottom: 4 }}>Email</label>
                        <input type="email" required value={contactForm.email} onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} placeholder="john@example.com" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--black-border-light)', background: 'var(--black-bg-card-4)', color: 'var(--text-on-black)' }} />
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <label className="settings-field-label" style={{ display: 'block', marginBottom: 4 }}>Phone</label>
                        <input type="text" value={contactForm.phone} onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))} placeholder="9876543210" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--black-border-light)', background: 'var(--black-bg-card-4)', color: 'var(--text-on-black)' }} />
                      </div>
                      {contactSaveMsg.text && <p style={{ color: contactSaveMsg.type === 'error' ? '#ef4444' : '#10b981', fontSize: 13, marginBottom: 12 }}>{contactSaveMsg.text}</p>}
                      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button type="button" className="btn btn-secondary" onClick={() => setShowContactModal(false)}>Cancel</button>
                        <button type="submit" className="btn btn-primary">{editingContactId ? 'Update' : 'Add'}</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </section>
          )}

          {tab === 'reports' && (
            <section className="table-card" style={{ padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <FiBarChart2 size={28} color={branding.primary_color || '#FFD600'} />
                  <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-on-black)' }}>Reports</h2>
                </div>
                {!reportsLoading && (
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setShowReportsExportMenu(!showReportsExportMenu)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <FiDownload size={18} /> Export
                    </button>
                    {showReportsExportMenu && (
                      <>
                        <div className="dropdown-backdrop" style={{ zIndex: 100 }} onClick={() => setShowReportsExportMenu(false)} />
                        <div
                          className="profile-dropdown"
                          style={{ right: 0, left: 'auto', minWidth: 200, zIndex: 101 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="profile-links" style={{ padding: 8 }}>
                            <button
                              type="button"
                              onClick={() => {
                                exportReportsExcel(reportsData);
                                setShowReportsExportMenu(false);
                              }}
                            >
                              Export as Excel (.xlsx)
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                exportReportsCSV(reportsData);
                                setShowReportsExportMenu(false);
                              }}
                            >
                              Export as CSV
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                exportReportsPDF(reportsData, branding.product_name || 'SignTada');
                                setShowReportsExportMenu(false);
                              }}
                            >
                              Export as PDF report
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                exportReportsJSON(reportsData);
                                setShowReportsExportMenu(false);
                              }}
                            >
                              Export as JSON
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              {reportsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                  <SigningLoader size="md" label="Loading reports..." />
                </div>
              ) : (
                <>
                  {/* KPI cards: Avg time-to-sign, Decline rate */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
                    <div className="chart-card" style={{ padding: 20 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-on-black-muted)', marginBottom: 6 }}>Avg. time to sign</div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-on-black)' }}>
                        {reportsData.avg_time_to_sign_hours != null
                          ? `${reportsData.avg_time_to_sign_hours} hrs`
                          : '—'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-on-black-muted)', marginTop: 4 }}>Send → completion (signed docs only)</div>
                    </div>
                    <div className="chart-card" style={{ padding: 20 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-on-black-muted)', marginBottom: 6 }}>Decline rate</div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: reportsData.decline_rate_percent > 10 ? '#EF4444' : 'var(--text-on-black)' }}>
                        {reportsData.decline_rate_percent != null ? `${reportsData.decline_rate_percent}%` : '—'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-on-black-muted)', marginTop: 4 }}>Rejected ÷ (signed + rejected)</div>
                    </div>
                  </div>

                  {/* Signing velocity + Templates breakdown */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 24, marginBottom: 28, alignItems: 'start' }}>
                    <div className="chart-card">
                      <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Signing velocity</h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={reportsData.signing_velocity || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--black-border-light)" />
                          <XAxis dataKey="date" stroke="var(--text-on-black-muted)" fontSize={11} />
                          <YAxis stroke="var(--text-on-black-muted)" fontSize={11} />
                          <ReTooltip formatter={(v) => [`${v} signed`, 'Signed']} contentStyle={{ background: 'var(--black-bg-card-3)', border: '1px solid var(--black-border)' }} labelStyle={{ color: 'var(--text-on-black)' }} />
                          <Area type="monotone" dataKey="count" stroke={branding.primary_color || '#FFD600'} fill={`${(branding.primary_color || '#FFD600')}26`} strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="chart-card">
                      <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Templates usage</h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={reportsData.templates_breakdown || []}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={85}
                            label
                          >
                            {(reportsData.templates_breakdown || []).map((entry, index) => (
                              <Cell key={entry.name || index} fill={index === 0 ? (branding.primary_color || '#FFD600') : '#64748b'} />
                            ))}
                          </Pie>
                          <Legend />
                          <ReTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Monthly document volume vs credits used */}
                  <div className="chart-card">
                    <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Monthly volume vs credits used</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={reportsData.monthly_volume || []} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--black-border-light)" />
                        <XAxis dataKey="month" stroke="var(--text-on-black-muted)" fontSize={11} />
                        <YAxis stroke="var(--text-on-black-muted)" fontSize={11} />
                        <ReTooltip
                          cursor={{ fill: 'rgba(255, 214, 0, 0.10)' }}
                          contentStyle={{ background: 'var(--black-bg-card-2)', border: '1px solid var(--black-border-light)' }}
                          labelStyle={{ color: 'var(--text-on-black)' }}
                          itemStyle={{ color: 'var(--text-on-black-secondary)' }}
                        />
                        <Legend />
                        <Bar dataKey="documents_created" name="Documents created" fill={branding.primary_color || '#FFD600'} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="credits_used" name="Credits used" fill="#10B981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </section>
          )}

          {tab === 'signingLinks' && (
            <section className="table-card">
              <h2 className="table-card-title">Signing Links</h2>
              <p style={{ color: 'var(--muted)', marginBottom: 16, fontSize: 14 }}>Public links that allow anyone with the link to sign. Signer name and email are captured before signing.</p>
              {signingLinksLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                  <SigningLoader size="md" label="Loading..." />
                </div>
              ) : signingLinksDocs.length === 0 ? (
                <div className="empty-state-card">
                  <div className="empty-state-icon"><FiLink size={32} /></div>
                  <div className="empty-state-title">No signing links yet</div>
                  <div className="empty-state-desc">Public signing links let anyone sign with just a URL — no email required. Enable it when creating a new request.</div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setTab('initiate')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FiPlusCircle size={13} /> New Request</button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Document</th>
                        <th>Reference</th>
                        <th>Status</th>
                        <th>Link</th>
                        <th>Disable link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signingLinksDocs.map((doc) => {
                        const enabled = doc.open_link_enabled !== false;
                        const refId = doc.reference_id;
                        const linkToken = doc.open_link_token;
                        const signingUrl = linkToken ? `${window.location.origin}/sign/${refId}?link=${encodeURIComponent(linkToken)}` : '';
                        return (
                          <tr key={doc.reference_id}>
                            <td>{doc.document_name || 'Document'}</td>
                            <td><code style={{ fontSize: 12 }}>{refId}</code></td>
                            <td>{doc.status || 'Pending'}</td>
                            <td>
                              {signingUrl ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => copySigningLink(refId, linkToken)}
                                  title="Copy signing link"
                                >
                                  <FiCopy size={14} style={{ marginRight: 4 }} /> {copiedLinkRefId === refId ? 'Copied!' : 'Copy link'}
                                </button>
                              ) : '—'}
                            </td>
                            <td>
                              <label className="switch" style={{ margin: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={enabled}
                                  disabled={signingLinksToggling === refId}
                                  onChange={() => toggleOpenLinkEnabled(refId, enabled)}
                                />
                                <span className="slider"></span>
                              </label>
                              {!enabled && <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--muted)' }}>Disabled</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {signingLinksTotalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                  <button className="btn btn-sm btn-secondary" disabled={signingLinksPage <= 1} onClick={() => { setSigningLinksPage(p => p - 1); loadSigningLinks(signingLinksPage - 1); }}>Prev</button>
                  <span style={{ alignSelf: 'center' }}>Page {signingLinksPage} of {signingLinksTotalPages}</span>
                  <button className="btn btn-sm btn-secondary" disabled={signingLinksPage >= signingLinksTotalPages} onClick={() => { setSigningLinksPage(p => p + 1); loadSigningLinks(signingLinksPage + 1); }}>Next</button>
                </div>
              )}
            </section>
          )}

          {tab === 'support' && (
            <SupportHub selectedAdminCompany={selectedAdminCompany} onSupportUnread={setSupportUnreadCount} />
          )}

          {tab === 'contracts' && (
             <section className="table-card">
             <div className="table-header-row">
                <h3>{getDocumentsHeaderText()}</h3>
                <div className="tabs-container">
                  <button className={`tab-btn ${filterStatus === 'ALL' ? 'active' : ''}`} onClick={() => { setFilterStatus('ALL'); setPage(1); load(1, { includeAnalytics: false, statusOverride: 'ALL' }); }}>All</button>
                  <button className={`tab-btn ${filterStatus === 'SIGNED' ? 'active' : ''}`} onClick={() => { setFilterStatus('SIGNED'); setPage(1); load(1, { includeAnalytics: false, statusOverride: 'SIGNED' }); }}>Signed</button>
                  <button className={`tab-btn ${filterStatus === 'PENDING' ? 'active' : ''}`} onClick={() => { setFilterStatus('PENDING'); setPage(1); load(1, { includeAnalytics: false, statusOverride: 'PENDING' }); }}>Pending</button>
                  <button className={`tab-btn ${filterStatus === 'REJECTED' ? 'active' : ''}`} onClick={() => { setFilterStatus('REJECTED'); setPage(1); load(1, { includeAnalytics: false, statusOverride: 'REJECTED' }); }}>Rejected</button>
                </div>
             </div>
             {docsLoading ? (
               <div style={{ padding: '30px 10px' }}>
                 <SigningLoader size="sm" label="Loading documents..." />
               </div>
             ) : filteredDocs.length === 0 ? (
               <div className="empty-state-card">
                 <div className="empty-state-icon"><FiFileText size={32} /></div>
                 <div className="empty-state-title">No documents match</div>
                 <div className="empty-state-desc">Try a different filter or create a new request to get started.</div>
                 <button type="button" className="btn btn-primary btn-sm" onClick={() => setFilterStatus('ALL')}>Show all</button>
                 <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={() => setTab('initiate')}>New Request</button>
               </div>
             ) : (
               <>
                 <table className="docs-table">
                   <thead>
                     <tr>
                       <th>Reference ID</th>
                       <th>Document Name</th>
                       <th>Created Date</th>
                       <th>Status</th>
                       <th style={{textAlign:'center'}}>Actions</th>
                     </tr>
                   </thead>
                   <tbody className="animated-tbody">
                     {filteredDocs.map((doc) => {
                       const refId = doc.reference_id || doc.referenceId || 'N/A';
                       
                       // Calculate partial completion status
                       const recipients = doc.recipients || [];
                       const signedRecipients = recipients.filter(r => getRecipientStatusValue(r) === 'Signed').length;
                       const rejectedRecipients = recipients.filter(r => getRecipientStatusValue(r) === 'Rejected').length;
                       const totalRecipients = recipients.length;
                       const isFullySigned = doc.status === 'Signed' || (totalRecipients > 0 && signedRecipients === totalRecipients);
                       const isPartiallySigned = totalRecipients > 0 && signedRecipients > 0 && signedRecipients < totalRecipients;
                       const hasRejected = rejectedRecipients > 0;
                       
                       let statusClass = isFullySigned ? 'signed' : 'pending';
                       let statusText = 'Pending';
                       if (hasRejected && !isFullySigned) {
                         statusClass = 'rejected';
                         statusText = rejectedRecipients === totalRecipients ? 'Rejected' : `${rejectedRecipients} Rejected`;
                       } else if (isFullySigned) {
                         statusText = 'Completed';
                       } else if (isPartiallySigned) {
                         statusText = `${signedRecipients}/${totalRecipients} Signed`;
                       }

                       const isExpanded = expandedDocId === refId;
                       return (
                         <React.Fragment key={refId}>
                           <tr
                             className={`table-row-main docs-table-clickable${isExpanded ? ' doc-row-expanded' : ''}`}
                             onClick={() => toggleRow(refId)}
                           >
                             <td style={{ fontWeight: 600 }}>{refId}</td>
                             <td>{doc.document_name || 'Document'}</td>
                             <td>{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}</td>
                             <td>
                               <span className={`status-badge ${statusClass}`}>
                                 {statusText}
                               </span>
                             </td>
                             <td className="actions-cell">
                               <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={e => e.stopPropagation()}>
                                 <span style={{ fontSize: 11, color: 'var(--text-on-black-muted)' }}>
                                   {isExpanded ? 'Collapse' : 'Details'}
                                 </span>
                                 <FiChevronDown
                                   size={14}
                                   style={{ color: 'var(--text-on-black-muted)', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                 />
                               </div>
                             </td>
                           </tr>
                           {isExpanded && (
                             <tr className="doc-detail-row">
                               <td colSpan={5} style={{ padding: 0, border: 'none' }}>
                                 <div className="doc-detail-panel">
                                   {/* Recipients */}
                                   <div className="doc-detail-section">
                                     <div className="doc-detail-label">Recipients</div>
                                     {recipients.length === 0 ? (
                                       <span style={{ color: 'var(--text-on-black-muted)', fontSize: 13 }}>—</span>
                                     ) : (
                                       <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                         {recipients.map((r, ri) => {
                                           const recipientBadge = getRecipientBadgeMeta(r);
                                           return (
                                           <div key={r.email || ri} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                             <div className="recipient-avatar-mini" style={{ flexShrink: 0 }}>
                                               {(r.email || r.name || '?')[0].toUpperCase()}
                                             </div>
                                             <div>
                                               <div style={{ fontSize: 13, color: 'var(--text-on-black)', fontWeight: 500 }}>{r.name || r.email}</div>
                                               {r.name && <div style={{ fontSize: 11, color: 'var(--text-on-black-muted)' }}>{r.email}</div>}
                                             </div>
                                             <span className={`status-badge ${recipientBadge.className}`} style={{ fontSize: 10, padding: '2px 6px', marginLeft: 'auto' }}>
                                               {recipientBadge.label}
                                             </span>
                                           </div>
                                         )})}
                                       </div>
                                     )}
                                   </div>
                                   {/* Meta */}
                                   <div className="doc-detail-section">
                                     <div className="doc-detail-label">Details</div>
                                     <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                                       <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                         <span style={{ color: 'var(--text-on-black-muted)' }}>Document ID</span>
                                         <span style={{ color: 'var(--text-on-black)', fontFamily: 'monospace', fontSize: 12 }}>{refId}</span>
                                       </div>
                                       <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                         <span style={{ color: 'var(--text-on-black-muted)' }}>Created</span>
                                         <span style={{ color: 'var(--text-on-black)' }}>{doc.created_at ? new Date(doc.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</span>
                                       </div>
                                       <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                         <span style={{ color: 'var(--text-on-black-muted)' }}>Status</span>
                                         <span className={`status-badge ${statusClass}`} style={{ fontSize: 10, padding: '2px 8px' }}>{statusText}</span>
                                       </div>
                                       <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                         <span style={{ color: 'var(--text-on-black-muted)' }}>Signed</span>
                                         <span style={{ color: 'var(--text-on-black)' }}>{signedRecipients} / {totalRecipients}</span>
                                       </div>
                                     </div>
                                   </div>
                                   {/* Actions */}
                                   <div className="doc-detail-actions">
                                     <button type="button" className="btn btn-primary" style={{ fontSize: 13, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => navigate(`/documents/${refId}`)}>
                                       <FiEye size={13} /> View Document
                                     </button>
                                     {getDownloadLink(doc) && (
                                       <a href={getDownloadLink(doc)} className="btn btn-secondary" style={{ fontSize: 13, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }} target="_blank" rel="noreferrer">
                                         <FiDownload size={13} /> Download
                                       </a>
                                     )}
                                     <a href={getAuditTrailLink(refId)} className="btn btn-secondary" style={{ fontSize: 13, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }} target="_blank" rel="noreferrer">
                                       <FiActivity size={13} /> Audit Trail
                                     </a>
                                     {doc.status !== 'Signed' && (
                                       <button
                                         type="button"
                                         className="btn btn-secondary"
                                         style={{ fontSize: 13, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                         disabled={resendingNotifId === refId}
                                         onClick={() => handleResendNotification(refId)}
                                       >
                                         <FiMail size={13} /> {resendingNotifId === refId ? 'Sending…' : 'Resend Email'}
                                       </button>
                                     )}
                                   </div>
                                 </div>
                               </td>
                             </tr>
                           )}
                         </React.Fragment>
                       );
                     })}
                   </tbody>
                 </table>
                 {tab === 'contracts' && (
                   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 12 }}>
                     <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                       {totalPages > 1 && (
                         <div className="pagination" style={{ marginTop: 0 }}>
                           <button className="pagination-btn" disabled={docsLoading || page === 1} onClick={() => handlePageChange(page - 1)}>
                             <FiChevronDown style={{ transform: 'rotate(90deg)', marginRight: 4 }} size={14} /> Prev
                           </button>
                           <div className="pagination-info">
                             <span className="pagination-current">{page}</span>
                             <span className="pagination-sep">of</span>
                             <span className="pagination-total">{totalPages}</span>
                             <span className="pagination-sep">|</span>
                             <span className="pagination-total">{totalDocs} docs</span>
                           </div>
                           <button className="pagination-btn" disabled={docsLoading || page === totalPages} onClick={() => handlePageChange(page + 1)}>
                             Next <FiChevronDown style={{ transform: 'rotate(-90deg)', marginLeft: 4 }} size={14} />
                           </button>
                         </div>
                       )}
                     </div>
                     <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                       <label style={{ fontSize: 12, color: 'var(--text-on-black-muted)' }}>Rows</label>
                       <select
                         className="admin-select"
                         value={rowsPerPage}
                         onChange={(e) => {
                           const nextRows = Number(e.target.value) || 10;
                           setRowsPerPage(nextRows);
                           setPage(1);
                           load(1, { includeAnalytics: false, statusOverride: filterStatus });
                         }}
                       >
                         <option value={10}>10</option>
                         <option value={25}>25</option>
                         <option value={50}>50</option>
                         <option value={100}>100</option>
                       </select>
                     </div>
                   </div>
                 )}
               </>
             )}
           </section>
          )}

          {tab === 'initiate' && (
            <div className="initiate-wrapper">
              <InitiateRequest
                onExit={() => setTab('contracts')}
                preSelectedOrgId={selectedAdminCompany}
                initialUseTemplate={!!initialTemplateIdForInitiate}
                initialTemplateId={initialTemplateIdForInitiate}
              />
            </div>
          )}

          {tab === 'batchSend' && (
            <div className="initiate-wrapper">
              <BulkRequest
                onExit={() => setTab('contracts')}
                preSelectedOrgId={selectedAdminCompany}
              />
            </div>
          )}

          {tab === 'apiKeys' && (
            <div className="settings-container">
              <div style={{marginBottom: 8}}>
                <h2 style={{margin: 0, fontSize: 28, fontWeight: 700, color: 'var(--text-on-black)'}}>API Keys</h2>
                <p style={{margin: '4px 0 0 0', fontSize: 14, color: 'var(--text-on-black-muted)'}}>Tokens for API authentication</p>
              </div>
              <div className="settings-section-card">
                <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20}}>
                  <div style={{background: `${(branding.primary_color || '#FFD600')}20`, padding: 10, borderRadius: 12, color: branding.primary_color || '#FFD600'}}><FiKey size={20} /></div>
                  <h3 style={{margin: 0, fontSize: 18, fontWeight: 600}}>Authentication Tokens</h3>
                </div>
                <p style={{margin: '0 0 16px 0', fontSize: 13, color: 'var(--text-on-black-muted)'}}>These tokens are used for API authentication. They are automatically refreshed when needed.</p>
                <div style={{display: 'grid', gap: 12}}>
                  <div className="settings-field-box">
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                      <FiKey size={16} color={branding.primary_color || '#FFD600'} />
                      <span className="settings-field-label">Access Token (JWT)</span>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                      <code style={{fontFamily: 'monospace', fontSize: 12, color: 'var(--text-on-black-secondary)', background: 'var(--black-bg-card-4)', padding: '6px 10px', borderRadius: 6, flex: 1, minWidth: 200, wordBreak: 'break-all', border: '1px solid var(--black-border)'}}>
                        {localStorage.getItem('authToken') ? (localStorage.getItem('authToken').substring(0, 20) + '...' + localStorage.getItem('authToken').substring(localStorage.getItem('authToken').length - 10)) : 'Not available'}
                      </code>
                      {localStorage.getItem('authToken') && (
                        <button className="btn btn-secondary btn-sm" onClick={() => { copyToClipboard(localStorage.getItem('authToken')); }} style={{fontSize: 11, padding: '4px 8px'}}>
                          <FiCopy size={12} style={{marginRight: 4}} /> Copy
                        </button>
                      )}
                    </div>
                    <p style={{margin: '6px 0 0 0', fontSize: 11, color: 'var(--text-on-black-muted)'}}>Used in Authorization header for all API requests</p>
                  </div>
                  <div className="settings-field-box">
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                      <FiRefreshCcw size={16} color={branding.primary_color || '#FFD600'} />
                      <span className="settings-field-label">Refresh Token</span>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                      <code style={{fontFamily: 'monospace', fontSize: 12, color: 'var(--text-on-black-secondary)', background: 'var(--black-bg-card-4)', padding: '6px 10px', borderRadius: 6, flex: 1, minWidth: 200, wordBreak: 'break-all', border: '1px solid var(--black-border)'}}>
                        {localStorage.getItem('refreshToken') ? (localStorage.getItem('refreshToken').substring(0, 20) + '...' + localStorage.getItem('refreshToken').substring(localStorage.getItem('refreshToken').length - 10)) : 'Not available'}
                      </code>
                      {localStorage.getItem('refreshToken') && (
                        <button className="btn btn-secondary btn-sm" onClick={() => { copyToClipboard(localStorage.getItem('refreshToken')); }} style={{fontSize: 11, padding: '4px 8px'}}>
                          <FiCopy size={12} style={{marginRight: 4}} /> Copy
                        </button>
                      )}
                    </div>
                    <p style={{margin: '6px 0 0 0', fontSize: 11, color: 'var(--text-on-black-muted)'}}>Used to obtain new access tokens when they expire</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'team' && (
            <section className="table-card">
              <div className="table-header-row">
                <h3>Team</h3>
                {(isAdmin || isSuperAdmin) && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={openCreateUserModal}>
                    <FiUserPlus style={{ marginRight: 6 }} /> Invite User
                  </button>
                )}
              </div>
              {!(isAdmin || isSuperAdmin) ? (
                <div className="empty-state-card">
                  <div className="empty-state-icon"><FiUsers size={32} /></div>
                  <div className="empty-state-title">Access Restricted</div>
                  <div className="empty-state-desc">Only admins can manage team members. Contact your administrator.</div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                      <FiSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-on-black-muted)', pointerEvents: 'none' }} />
                      <input
                        type="text"
                        placeholder="Search by email or role..."
                        value={teamSearch}
                        onChange={e => setTeamSearch(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px 8px 34px', borderRadius: 8, border: '1px solid var(--black-border)', background: 'var(--black-bg-card-2)', color: 'var(--text-on-black)', fontSize: 13 }}
                      />
                    </div>
                    <select value={teamRoleFilter} onChange={e => setTeamRoleFilter(e.target.value)} className="admin-select">
                      <option value="ALL">All Roles</option>
                      <option value="USER">User</option>
                      <option value="ADMIN">Admin</option>
                      {isSuperAdmin && <option value="SUPER_ADMIN">Super Admin</option>}
                    </select>
                  </div>
                  {getFilteredUsers().length === 0 ? (
                    <div className="empty-state-card">
                      <div className="empty-state-icon"><FiUsers size={32} /></div>
                      <div className="empty-state-title">No users found</div>
                      <div className="empty-state-desc">Invite a team member to get started.</div>
                      <button type="button" className="btn btn-primary btn-sm" onClick={openCreateUserModal}><FiUserPlus style={{ marginRight: 6 }} /> Invite User</button>
                    </div>
                  ) : (
                    <table className="docs-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Joined</th>
                          <th style={{ textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className="animated-tbody">
                        {getFilteredUsers().map(u => (
                          <tr key={u.id}>
                            <td style={{ fontWeight: 600 }}>{u.email.split('@')[0]}</td>
                            <td>{u.email}</td>
                            <td>
                              <span style={{
                                background: u.roles[0] === 'SUPER_ADMIN' ? 'rgba(239,68,68,0.15)' : u.roles[0] === 'ADMIN' ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.15)',
                                color: u.roles[0] === 'SUPER_ADMIN' ? '#ef4444' : u.roles[0] === 'ADMIN' ? '#d97706' : '#3b82f6',
                                border: u.roles[0] === 'SUPER_ADMIN' ? '1px solid rgba(239,68,68,0.4)' : u.roles[0] === 'ADMIN' ? '1px solid rgba(245,158,11,0.4)' : '1px solid rgba(59,130,246,0.4)',
                                padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700
                              }}>
                                {u.roles[0] || 'USER'}
                              </span>
                            </td>
                            <td>
                              <span style={{ color: u.isActive ? '#10b981' : '#ef4444', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 500 }}>
                                {u.isActive ? <FiCheckCircle size={13} /> : <FiXCircle size={13} />}
                                {u.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td style={{ fontSize: 13 }}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
                            <td className="actions-cell">
                              <div className="action-btn-group">
                                <button className="btn-action btn-audit" onClick={() => openEditUserModal(u)} style={{ padding: '4px 10px', fontSize: 12 }}><FiEdit2 style={{ marginRight: 4 }} />Edit</button>
                                <button
                                  className="btn-action btn-audit"
                                  onClick={() => handleToggleUserActive(u.id, u.isActive)}
                                  disabled={togglingUserId === u.id}
                                  style={{ padding: '4px 10px', fontSize: 12, color: u.isActive ? '#f59e0b' : '#10b981' }}
                                >
                                  {togglingUserId === u.id ? '...' : u.isActive ? 'Deactivate' : 'Reactivate'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {showUserModal && (
                    <div className="modal-backdrop" onClick={() => setShowUserModal(false)}>
                      <div className="modal-content" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                          <h3 style={{ margin: 0 }}>{userModalMode === 'create' ? 'Invite User' : 'Edit User'}</h3>
                          <button type="button" className="icon-btn-header" onClick={() => setShowUserModal(false)}><FiX /></button>
                        </div>
                        {userModalMode === 'create' && inviteLink ? (
                          <div>
                            <p style={{ color: '#10b981', fontSize: 14, marginBottom: 16 }}>Invite sent successfully! Share this link with the user:</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                              <input type="text" readOnly value={inviteLink}
                                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--black-border-light)', background: 'var(--black-bg-card-4)', color: 'var(--text-on-black)', fontSize: 13, boxSizing: 'border-box' }} />
                              <button type="button" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
                                onClick={() => { navigator.clipboard.writeText(inviteLink); }}>
                                <FiCopy size={14} /> Copy Link
                              </button>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <button type="button" className="btn btn-secondary" onClick={() => { setShowUserModal(false); setInviteLink(''); }}>Close</button>
                            </div>
                          </div>
                        ) : (
                        <form onSubmit={handleUserModalSubmit}>
                          <div style={{ marginBottom: 14 }}>
                            <label className="settings-field-label" style={{ display: 'block', marginBottom: 4 }}>Email</label>
                            <input type="email" required value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))}
                              placeholder="user@company.com"
                              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--black-border-light)', background: 'var(--black-bg-card-4)', color: 'var(--text-on-black)', boxSizing: 'border-box' }} />
                          </div>
                          {userModalMode === 'edit' && (
                          <div style={{ marginBottom: 14 }}>
                            <label className="settings-field-label" style={{ display: 'block', marginBottom: 4 }}>
                              Password <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-on-black-muted)' }}>(leave blank to keep current)</span>
                            </label>
                            <input type="password" value={userForm.password} onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))}
                              placeholder="New password (optional)"
                              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--black-border-light)', background: 'var(--black-bg-card-4)', color: 'var(--text-on-black)', boxSizing: 'border-box' }} />
                          </div>
                          )}
                          <div style={{ marginBottom: 14 }}>
                            <label className="settings-field-label" style={{ display: 'block', marginBottom: 4 }}>Role</label>
                            <select value={userForm.role} onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))}
                              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--black-border-light)', background: 'var(--black-bg-card-4)', color: 'var(--text-on-black)' }}>
                              <option value="USER">User (Standard)</option>
                              <option value="ADMIN">Admin</option>
                              {isSuperAdmin && <option value="SUPER_ADMIN">Super Admin</option>}
                            </select>
                          </div>
                          {isSuperAdmin && userForm.role !== 'SUPER_ADMIN' && (
                            <div style={{ marginBottom: 14 }}>
                              <label className="settings-field-label" style={{ display: 'block', marginBottom: 4 }}>Assign to Company</label>
                              <select value={userForm.companyId} onChange={e => setUserForm(f => ({ ...f, companyId: e.target.value }))}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--black-border-light)', background: 'var(--black-bg-card-4)', color: 'var(--text-on-black)' }}>
                                <option value="">-- Select Company --</option>
                                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                              </select>
                            </div>
                          )}
                          {userFormError && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{userFormError}</p>}
                          {userFormSuccess && <p style={{ color: '#10b981', fontSize: 13, marginBottom: 10 }}>{userFormSuccess}</p>}
                          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn-secondary" onClick={() => setShowUserModal(false)}>Cancel</button>
                            <button type="submit" className="btn btn-primary" disabled={userFormSaving}>
                              {userFormSaving ? 'Saving...' : userModalMode === 'create' ? 'Send Invite' : 'Save Changes'}
                            </button>
                          </div>
                        </form>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {tab === 'billing' && (
            <div className="settings-container">
              <div style={{marginBottom: 24}}>
                <h2 style={{margin: 0, fontSize: 28, fontWeight: 700, color: 'var(--text-on-black)'}}>Billing</h2>
                <p style={{margin: '4px 0 0 0', fontSize: 14, color: 'var(--text-on-black-muted)'}}>Your plan, credits, and usage</p>
              </div>
              {billingLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                  <SigningLoader size="md" label="Loading billing info..." />
                </div>
              ) : (
                <>
                  {/* Plan + stats cards */}
                  <div className="settings-section-card" style={{marginBottom: 20}}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                        <div style={{background: `${(branding.primary_color || '#FFD600')}20`, padding: 10, borderRadius: 12, color: branding.primary_color || '#FFD600'}}><FiCreditCard size={20} /></div>
                        <div>
                          <div style={{fontSize: 12, color: 'var(--text-on-black-muted)', marginBottom: 2}}>Current Plan</div>
                          <div style={{fontSize: 22, fontWeight: 800, color: 'var(--text-on-black)'}}>{billingInfo.plan || 'Free'}</div>
                        </div>
                      </div>
                      <button type="button" className="btn btn-primary" disabled style={{opacity: 0.8, fontSize: 13}}>Upgrade Plan</button>
                    </div>
                    {/* Credits usage bar */}
                    {(() => {
                      const total = billingInfo.total_credits || 100;
                      const used = billingInfo.usage_this_month || 0;
                      const available = typeof billingInfo.credits === 'number' ? billingInfo.credits : (total - used);
                      const pct = Math.min(100, Math.round((used / total) * 100));
                      const barColor = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f97316' : branding.primary_color || '#FFD600';
                      return (
                        <div>
                          <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13}}>
                            <span style={{color: 'var(--text-on-black-muted)'}}>Credits used this month</span>
                            <span style={{color: 'var(--text-on-black)', fontWeight: 600}}>{used} / {total}</span>
                          </div>
                          <div style={{height: 8, borderRadius: 4, background: 'var(--black-bg-card-4)', overflow: 'hidden'}}>
                            <div style={{height: '100%', width: `${pct}%`, background: barColor, borderRadius: 4, transition: 'width 0.4s ease'}} />
                          </div>
                          <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: 'var(--text-on-black-muted)'}}>
                            <span>{pct}% used</span>
                            <span style={{color: barColor, fontWeight: 600}}>{available} remaining</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Stats row */}
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 20}}>
                    {[
                      { icon: <FiActivity size={18} />, label: 'Used this month', value: billingInfo.usage_this_month ?? 0, unit: 'signatures' },
                      { icon: <FiCheckCircle size={18} />, label: 'Available credits', value: typeof billingInfo.credits === 'number' ? billingInfo.credits.toFixed(0) : '—', unit: 'credits' },
                      { icon: <FiClock size={18} />, label: 'Credits reset', value: 'Monthly', unit: 'cycle' },
                    ].map(({ icon, label, value, unit }) => (
                      <div key={label} className="settings-field-box" style={{padding: 16}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, color: branding.primary_color || '#FFD600'}}>{icon}<span className="settings-field-label">{label}</span></div>
                        <div style={{fontSize: 22, fontWeight: 700, color: 'var(--text-on-black)'}}>{value}</div>
                        <div style={{fontSize: 11, color: 'var(--text-on-black-muted)', marginTop: 2}}>{unit}</div>
                      </div>
                    ))}
                  </div>

                  {/* Buy credits CTA */}
                  <div className="settings-section-card" style={{background: `linear-gradient(135deg, ${(branding.primary_color || '#FFD600')}10, transparent)`}}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16}}>
                      <div>
                        <h3 style={{margin: '0 0 6px 0', fontSize: 16, fontWeight: 700}}>Need more credits?</h3>
                        <p style={{margin: 0, fontSize: 13, color: 'var(--text-on-black-muted)'}}>Purchase additional credits. Payment integration coming soon.</p>
                      </div>
                      <button type="button" className="btn btn-primary" disabled style={{opacity: 0.8, flexShrink: 0}}>Buy Credits (coming soon)</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === 'settings' && (
            <div className="settings-container">
              <div style={{marginBottom: 8}}>
                <h2 style={{margin: 0, fontSize: 28, fontWeight: 700, color: 'var(--text-on-black)'}}>Settings</h2>
                <p style={{margin: '4px 0 0 0', fontSize: 14, color: 'var(--text-on-black-muted)'}}>Manage your account, organizations, and platform settings</p>
              </div>

              {/* Account Settings Section */}
              <div className="settings-section-card">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                    <div style={{background: `${(branding.primary_color || '#FFD600')}20`, padding: 10, borderRadius: 12, color: branding.primary_color || '#FFD600'}}><FiUser size={20} /></div>
                    <h3 style={{margin: 0, fontSize: 18, fontWeight: 600}}>Account Settings</h3>
                  </div>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16}}>
                  <div className="settings-field-box">
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                      <FiMail size={16} color={branding.primary_color || '#FFD600'} />
                      <span className="settings-field-label">Email Address</span>
                    </div>
                    <div className="settings-field-value">{userEmail}</div>
                  </div>
                  <div className="settings-field-box">
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                      <FiLock size={16} color={branding.primary_color || '#FFD600'} />
                      <span className="settings-field-label">Company ID</span>
                    </div>
                    <div className="settings-field-value">{orgId || 'N/A'}</div>
                  </div>
                  <div className="settings-field-box">
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                      <FiRadio size={16} color={branding.primary_color || '#FFD600'} />
                      <span className="settings-field-label">Role</span>
                    </div>
                    <div><span className="badge badge-info" style={{background: `${(branding.primary_color || '#FFD600')}20`, color: branding.primary_color || '#FFD600', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, border: `1px solid ${(branding.primary_color || '#FFD600')}60`}}>{userRoles.join(', ')}</span></div>
                  </div>
                  <div className="settings-field-box">
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                      <FiClock size={16} color={branding.primary_color || '#FFD600'} />
                      <span className="settings-field-label">Account Status</span>
                    </div>
                    <div><span style={{background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, border: '1px solid rgba(16,185,129,0.4)'}}>Active</span></div>
                  </div>
                </div>
                {!isSuperAdmin && (
                  <div className="settings-field-box" style={{marginTop: 16}}>
                    <span className="settings-field-label" style={{marginBottom: 4, display: 'block'}}>Available Credits</span>
                    <span className="settings-field-value" style={{fontSize: 20, fontWeight: 700}}>{availableCredits}</span>
                  </div>
                )}
              </div>

              {/* Branding & Theme (Admin / Super Admin only) */}
              {(isAdmin || isSuperAdmin) && (
                <div className="settings-section-card" style={{marginTop: 24}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20}}>
                    <div style={{background: `${(branding.primary_color || '#FFD600')}20`, padding: 10, borderRadius: 12, color: branding.primary_color || '#FFD600'}}><FiLayout size={20} /></div>
                    <h3 style={{margin: 0, fontSize: 18, fontWeight: 600}}>Branding & Theme</h3>
                  </div>
                  <div style={{display: 'grid', gap: 24, maxWidth: 520}}>
                    <div className="form-group">
                      <label className="settings-field-label" style={{marginBottom: 8, display: 'block', color: 'var(--text-on-black-muted)'}}>Product name</label>
                      <input
                        type="text"
                        value={brandingForm.product_name}
                        onChange={e => { setBrandingForm(f => ({ ...f, product_name: e.target.value })); setBrandingFormDirty(true); }}
                        placeholder="SignTada"
                        style={{padding: '10px 12px', borderRadius: 8, border: '1px solid var(--black-border-light)', background: 'var(--black-bg-card-4)', color: 'var(--text-on-black)', width: '100%'}}
                      />
                    </div>
                    <div className="form-group">
                      <label className="settings-field-label" style={{marginBottom: 8, display: 'block', color: 'var(--text-on-black-muted)'}}>Text logo style</label>
                      <div style={{display: 'flex', gap: 12, alignItems: 'center'}}>
                        <button
                          type="button"
                          onClick={() => { setBrandingForm(f => ({ ...f, text_logo_style: 'default', first_part_color: null, rest_part_color: null })); setBrandingFormDirty(true); }}
                          style={{
                            padding: '10px 16px', borderRadius: 8, border: brandingForm.text_logo_style === 'default' ? '2px solid var(--primary)' : '1px solid var(--black-border)',
                            background: brandingForm.text_logo_style === 'default' ? `${(branding.primary_color || '#FFD600')}18` : 'var(--black-bg-card-4)',
                            color: 'var(--text-on-black)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                          }}
                        >
                          Default
                        </button>
                        <button
                          type="button"
                          onClick={() => { setBrandingForm(f => ({ ...f, text_logo_style: 'custom', text_logo_custom_mode: f.text_logo_custom_mode || 'single', first_part_color: f.first_part_color || '#ffffff', rest_part_color: f.rest_part_color || f.primary_color || '#FFD600' })); setBrandingFormDirty(true); }}
                          style={{
                            padding: '10px 16px', borderRadius: 8, border: brandingForm.text_logo_style === 'custom' ? '2px solid var(--primary)' : '1px solid var(--black-border)',
                            background: brandingForm.text_logo_style === 'custom' ? `${(branding.primary_color || '#FFD600')}18` : 'var(--black-bg-card-4)',
                            color: 'var(--text-on-black)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                          }}
                        >
                          Custom
                        </button>
                      </div>
                      {brandingForm.text_logo_style === 'custom' && (
                        <div style={{ marginTop: 16, paddingLeft: 4 }}>
                          <div className="settings-field-label" style={{ marginBottom: 8, color: 'var(--text-on-black-muted)', fontSize: 12 }}>Color mode</div>
                          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                            <button
                              type="button"
                              onClick={() => { setBrandingForm(f => ({ ...f, text_logo_custom_mode: 'single', rest_part_color: f.first_part_color || '#ffffff' })); setBrandingFormDirty(true); }}
                              style={{
                                padding: '8px 14px', borderRadius: 6, border: brandingForm.text_logo_custom_mode === 'single' ? '1px solid var(--primary)' : '1px solid var(--black-border)',
                                background: 'var(--black-bg-card-4)', color: 'var(--text-on-black)', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                              }}
                            >
                              Single color
                            </button>
                            <button
                              type="button"
                              onClick={() => { setBrandingForm(f => ({ ...f, text_logo_custom_mode: 'dual', rest_part_color: f.rest_part_color || f.primary_color || '#FFD600' })); setBrandingFormDirty(true); }}
                              style={{
                                padding: '8px 14px', borderRadius: 6, border: brandingForm.text_logo_custom_mode === 'dual' ? '1px solid var(--primary)' : '1px solid var(--black-border)',
                                background: 'var(--black-bg-card-4)', color: 'var(--text-on-black)', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                              }}
                            >
                              Dual color
                            </button>
                          </div>
                          <div style={{ background: 'var(--black-bg-card-4)', border: '1px solid var(--black-border-light)', borderRadius: 8, padding: '12px 16px', maxWidth: 320 }}>
                            {brandingForm.text_logo_custom_mode === 'single' ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontSize: 12, color: 'var(--text-on-black-muted)', minWidth: 80 }}>Color</span>
                                <input type="color" value={brandingForm.first_part_color || '#ffffff'} onChange={e => { setBrandingForm(f => ({ ...f, first_part_color: e.target.value, rest_part_color: e.target.value })); setBrandingFormDirty(true); }} style={{ width: 36, height: 36, border: '1px solid var(--black-border)', borderRadius: 8, cursor: 'pointer' }} />
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <span style={{ fontSize: 12, color: 'var(--text-on-black-muted)', minWidth: 80 }}>First part</span>
                                  <input type="color" value={brandingForm.first_part_color || '#ffffff'} onChange={e => { setBrandingForm(f => ({ ...f, first_part_color: e.target.value })); setBrandingFormDirty(true); }} style={{ width: 36, height: 36, border: '1px solid var(--black-border)', borderRadius: 8, cursor: 'pointer' }} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <span style={{ fontSize: 12, color: 'var(--text-on-black-muted)', minWidth: 80 }}>Second part</span>
                                  <input type="color" value={brandingForm.rest_part_color || brandingForm.primary_color || '#FFD600'} onChange={e => { setBrandingForm(f => ({ ...f, rest_part_color: e.target.value })); setBrandingFormDirty(true); }} style={{ width: 36, height: 36, border: '1px solid var(--black-border)', borderRadius: 8, cursor: 'pointer' }} />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="settings-field-label" style={{marginBottom: 8, display: 'block', color: 'var(--text-on-black-muted)'}}>Logo</label>
                      <div style={{display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'}}>
                        {brandingForm.logo_url && (
                          <img src={getBrandingLogoUrl(brandingForm.logo_url)} alt="Logo" style={{maxHeight: 40, maxWidth: 120, objectFit: 'contain', border: '1px solid var(--black-border)', borderRadius: 6}} />
                        )}
                        <label style={{cursor: 'pointer'}}>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            style={{display: 'none'}}
                            onChange={async e => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setBrandingUploading(true);
                              try {
                                const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
                                const fd = new FormData();
                                fd.append('file', file);
                                const res = await axios.post(`${baseUrl}/files/upload`, fd);
                                setBrandingForm(f => ({ ...f, logo_url: res.data?.file_path || res.data?.public_url || null })); setBrandingFormDirty(true);
                              } catch (err) {
                                setBrandingSaveStatus({ type: 'error', message: 'Logo upload failed' });
                              } finally {
                                setBrandingUploading(false);
                                e.target.value = '';
                              }
                            }}
                          />
                          <span className="btn btn-secondary" style={{display: 'inline-block', padding: '8px 16px'}}>{brandingUploading ? <SigningLoader size="sm" label="" /> : (brandingForm.logo_url ? 'Change' : 'Upload')}</span>
                        </label>
                        {brandingForm.logo_url && (
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setBrandingForm(f => ({ ...f, logo_url: null })); setBrandingFormDirty(true); }}>Remove</button>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="settings-field-label" style={{marginBottom: 8, display: 'block', color: 'var(--text-on-black-muted)'}}>Theme</label>
                      {(() => {
                        const primary = branding.primary_color || '#FFD600';
                        const halfCircle = (p, a, title, key) => (
                          <span key={key} title={title} style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(90deg, ${p} 50%, ${a} 50%)`, border: '2px solid var(--black-border)', flexShrink: 0, display: 'inline-block' }} />
                        );
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                            {THEME_PRESETS.map((preset) => (
                              <button
                                key={preset.id}
                                type="button"
                                title={preset.label}
                                onClick={() => { setBrandingForm(f => ({ ...f, theme: preset.id, primary_color: preset.primary, accent_color: preset.accent })); setBrandingFormDirty(true); }}
                                style={{
                                  padding: 6, borderRadius: '50%', border: brandingForm.theme === preset.id ? `2px solid ${primary}` : '1px solid var(--black-border)',
                                  background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                }}
                              >
                                {halfCircle(preset.primary, preset.accent, preset.label, preset.id)}
                              </button>
                            ))}
                            {(brandingForm.custom_themes || []).map((ct, idx) => (
                              <div key={`ct-${idx}-${ct.name}`} style={{ position: 'relative', display: 'inline-flex' }}>
                                <button
                                  type="button"
                                  title={ct.name}
                                  onClick={() => { setBrandingForm(f => ({ ...f, theme: 'custom', primary_color: ct.primary_color, accent_color: ct.accent_color })); setBrandingFormDirty(true); }}
                                  style={{
                                    padding: 6, borderRadius: '50%', border: (brandingForm.theme === 'custom' && brandingForm.primary_color === ct.primary_color && brandingForm.accent_color === ct.accent_color) ? `2px solid ${primary}` : '1px solid var(--black-border)',
                                    background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  }}
                                >
                                  {halfCircle(ct.primary_color, ct.accent_color, ct.name, `ct-${idx}`)}
                                </button>
                                <button
                                  type="button"
                                  title="Remove theme"
                                  onClick={e => { e.stopPropagation(); setBrandingForm(f => ({ ...f, custom_themes: (f.custom_themes || []).filter((_, i) => i !== idx) })); setBrandingFormDirty(true); }}
                                  style={{ position: 'absolute', top: -4, right: -4, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'var(--danger)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, lineHeight: 1 }}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              title="Add theme"
                              onClick={() => { setAddThemeModalForm({ name: '', primary_color: brandingForm.primary_color || '#FFD600', accent_color: brandingForm.accent_color || '#FFAB00' }); setShowAddThemeModal(true); }}
                              style={{
                                padding: 6, borderRadius: '50%', border: '1px dashed var(--black-border)',
                                background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              }}
                            >
                              <span style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--black-bg-card-4)', border: '2px solid var(--black-border)', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-on-black-muted)', fontSize: 18, fontWeight: 700, lineHeight: 1 }}>+</span>
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                    {showAddThemeModal && (
                      <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowAddThemeModal(false)}>
                        <div className="modal-content" style={{ maxWidth: 400, width: '90%' }} onClick={e => e.stopPropagation()}>
                          <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Add theme</h3>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label className="settings-field-label" style={{ marginBottom: 6, display: 'block' }}>Theme name</label>
                              <input
                                type="text"
                                placeholder="e.g. Ocean"
                                value={addThemeModalForm.name}
                                onChange={e => setAddThemeModalForm(f => ({ ...f, name: e.target.value }))}
                                style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--gray-300)', width: '100%', fontSize: 14 }}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{ flex: 1 }}>
                                <label className="settings-field-label" style={{ marginBottom: 6, display: 'block', fontSize: 12 }}>Primary</label>
                                <input
                                  type="color"
                                  value={addThemeModalForm.primary_color}
                                  onChange={e => setAddThemeModalForm(f => ({ ...f, primary_color: e.target.value }))}
                                  style={{ width: 44, height: 40, border: '1px solid var(--gray-300)', borderRadius: 8, cursor: 'pointer' }}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label className="settings-field-label" style={{ marginBottom: 6, display: 'block', fontSize: 12 }}>Accent</label>
                                <input
                                  type="color"
                                  value={addThemeModalForm.accent_color}
                                  onChange={e => setAddThemeModalForm(f => ({ ...f, accent_color: e.target.value }))}
                                  style={{ width: 44, height: 40, border: '1px solid var(--gray-300)', borderRadius: 8, cursor: 'pointer' }}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span className="settings-field-label" style={{ fontSize: 12 }}>Preview</span>
                              <span style={{ width: 40, height: 40, borderRadius: 8, background: `linear-gradient(135deg, ${addThemeModalForm.primary_color} 50%, ${addThemeModalForm.accent_color} 50%)`, border: '1px solid var(--gray-300)', flexShrink: 0 }} />
                            </div>
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                              <button type="button" className="btn btn-ghost" onClick={() => setShowAddThemeModal(false)}>Cancel</button>
                              <button
                                type="button"
                                className="btn btn-primary"
                                disabled={!(addThemeModalForm.name || '').trim()}
                                onClick={() => {
                                  const name = (addThemeModalForm.name || '').trim();
                                  if (!name) return;
                                  setBrandingForm(f => ({
                                    ...f,
                                    custom_themes: [...(f.custom_themes || []), { name, primary_color: addThemeModalForm.primary_color, accent_color: addThemeModalForm.accent_color }],
                                  }));
                                  setBrandingFormDirty(true);
                                  setShowAddThemeModal(false);
                                  setAddThemeModalForm({ name: '', primary_color: '#FFD600', accent_color: '#FFAB00' });
                                }}
                              >
                                Add theme
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div style={{display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap'}}>
                      {brandingFormDirty && (
                        <>
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={brandingSaving}
                            onClick={async () => {
                              setBrandingSaveStatus({ type: '', message: '' });
                              setBrandingSaving(true);
                              const isDefaultTextStyle = brandingForm.text_logo_style === 'default';
                              const firstColor = isDefaultTextStyle ? null : (brandingForm.first_part_color || null);
                              const restColor = isDefaultTextStyle ? null : (brandingForm.text_logo_custom_mode === 'single' ? brandingForm.first_part_color : brandingForm.rest_part_color) || null;
                              const payload = {
                                product_name: brandingForm.product_name || 'SignTada',
                                logo_url: brandingForm.logo_url || null,
                                theme: brandingForm.theme,
                                primary_color: brandingForm.primary_color,
                                accent_color: brandingForm.accent_color,
                                show_powered_by: true,
                                first_part_color: firstColor,
                                rest_part_color: restColor,
                                custom_themes: brandingForm.custom_themes || [],
                              };
                              try {
                                const orgId = (isSuperAdmin && selectedAdminCompany) ? selectedAdminCompany : null;
                                await updateBranding(payload, orgId);
                                setBrandingForm({
                                  ...brandingForm,
                                  product_name: payload.product_name ?? brandingForm.product_name,
                                  logo_url: payload.logo_url !== undefined ? payload.logo_url : brandingForm.logo_url,
                                  theme: payload.theme,
                                  primary_color: payload.primary_color,
                                  accent_color: payload.accent_color,
                                  first_part_color: payload.first_part_color ?? brandingForm.first_part_color,
                                  rest_part_color: payload.rest_part_color ?? brandingForm.rest_part_color,
                                });
                                setBrandingSaveStatus({ type: 'success', message: 'Saved.' });
                                setBrandingFormDirty(false);
                              } catch (err) {
                                setBrandingSaveStatus({ type: 'error', message: err.message || 'Failed to save.' });
                              } finally {
                                setBrandingSaving(false);
                              }
                            }}
                          >
                            <FiCheck style={{marginRight: 6}} /> {brandingSaving ? <SigningLoader size="sm" label="" /> : 'Save'}
                          </button>
                          <button type="button" className="btn btn-ghost" onClick={() => { setBrandingFormDirty(false); setBrandingForm({ product_name: branding.product_name || 'SignTada', logo_url: branding.logo_url ?? null, theme: inferTheme(branding.primary_color, branding.accent_color), primary_color: branding.primary_color || '#FFD600', accent_color: branding.accent_color || '#FFAB00', show_powered_by: true, first_part_color: branding.first_part_color ?? '#ffffff', rest_part_color: branding.rest_part_color ?? (branding.primary_color || '#FFD600'), custom_themes: Array.isArray(branding.custom_themes) ? branding.custom_themes : [], custom_theme_name: '', text_logo_style: (branding.first_part_color == null && branding.rest_part_color == null) ? 'default' : 'custom', text_logo_custom_mode: (branding.first_part_color != null && branding.rest_part_color != null && branding.first_part_color === branding.rest_part_color) ? 'single' : 'dual' }); }}>Cancel</button>
                        </>
                      )}
                      {brandingSaveStatus.message && <span style={{fontSize: 13, color: brandingSaveStatus.type === 'error' ? 'var(--danger)' : 'var(--success)'}}>{brandingSaveStatus.message}</span>}
                    </div>
                  </div>
                </div>
              )}

              {/* Security Settings Section */}
              <div className="settings-section-card" style={{marginTop: 24}}>
                <h3 style={{margin: '0 0 20px 0', fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 12}}>
                  <div style={{background: `${(branding.primary_color || '#FFD600')}20`, padding: 10, borderRadius: 12, color: branding.primary_color || '#FFD600'}}><FiShield size={20} /></div>
                  Security Settings
                </h3>
                <div style={{display: 'grid', gap: 16}}>
                  <div className="settings-field-box" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <div>
                      <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <FiLock size={16} color={branding.primary_color || '#FFD600'} />
                        <span className="settings-field-label">Two-Factor Authentication</span>
                      </div>
                      <p style={{margin: 0, fontSize: 12, color: 'var(--text-on-black-muted)'}}>Add an extra layer of security</p>
                    </div>
                    <button className="btn btn-secondary" disabled style={{fontSize: 13, padding: '8px 16px', opacity: 0.5}}>Enable</button>
                  </div>
                  <div className="settings-field-box" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <div>
                      <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <FiKey size={16} color={branding.primary_color || '#FFD600'} />
                        <span className="settings-field-label">Change Password</span>
                      </div>
                      <p style={{margin: 0, fontSize: 12, color: 'var(--text-on-black-muted)'}}>Update your account password</p>
                    </div>
                    <button className="btn btn-secondary" onClick={() => setShowChangePassword(!showChangePassword)} style={{fontSize: 13, padding: '8px 16px'}}>Change</button>
                  </div>
                </div>
                {showChangePassword && (
                  <div style={{marginTop: 16, padding: 16, background: 'var(--black-bg-card-4)', borderRadius: 8, border: '1px solid var(--black-border)'}}>
                    <div className="form-group"><label>Current Password</label><input type="password" value={changePasswordData.current} onChange={e => setChangePasswordData({...changePasswordData, current: e.target.value})} /></div>
                    <div className="form-group"><label>New Password</label><input type="password" value={changePasswordData.new} onChange={e => setChangePasswordData({...changePasswordData, new: e.target.value})} /></div>
                    <div className="form-group"><label>Confirm New Password</label><input type="password" value={changePasswordData.confirm} onChange={e => setChangePasswordData({...changePasswordData, confirm: e.target.value})} /></div>
                    <div style={{display: 'flex', gap: 10}}>
                      <button className="btn btn-primary" onClick={() => { toast("Password change coming soon", 'info'); setShowChangePassword(false); }}>Update Password</button>
                      <button className="btn btn-secondary" onClick={() => { setShowChangePassword(false); setChangePasswordData({current: "", new: "", confirm: ""}); }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Super Admin: Organizations Management */}
              {isSuperAdmin && (
                <>
                  <div className="settings-section-card" style={{marginTop: 24}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                        <div style={{background: `${(branding.primary_color || '#FFD600')}20`, padding: 10, borderRadius: 12, color: branding.primary_color || '#FFD600'}}><FiBriefcase size={20} /></div>
                        <h3 style={{margin: 0, fontSize: 18, fontWeight: 600}}>Organizations</h3>
                      </div>
                      <button className="btn btn-primary" onClick={() => { setNewCompName(""); setNewCompSlug(""); setNewCompEmail(""); setNewCompAddress(""); setCompCreateMsg({type: '', text: ''}); window.scrollTo({top: document.querySelector('.settings-container').scrollHeight, behavior: 'smooth'}); }} style={{fontSize: 13, padding: '8px 16px'}}>
                        <FiPlusCircle style={{marginRight: 4}} /> Create Organization
                      </button>
                    </div>
                    {companies.length > 0 ? (
                      <div className="table-card" style={{boxShadow: 'none', border: '1px solid var(--black-border-light)', padding: 0}}>
                        <table className="docs-table">
                          <thead>
                            <tr>
                              <th>Company Name</th>
                              <th>Plan</th>
                              <th>Credits</th>
                              <th>Users</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {companies.map(c => (
                              <tr key={c.id}>
                                <td style={{fontWeight: 600}}>{c.name}</td>
                                <td><span style={{background: 'rgba(255, 214, 0, 0.2)', color: 'var(--primary)', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600}}>Enterprise</span></td>
                                <td style={{color: '#10b981', fontWeight: 600}}>{c.credits || 0}</td>
                                <td>{allUsers.filter(u => u.companyId === c.id).length}</td>
                                <td><span style={{color: '#10b981', display: 'flex', alignItems: 'center', gap: 4}}><FiCheckCircle /> Active</span></td>
                                <td>
                                  <div style={{display: 'flex', gap: 8}}>
                                    <button className="btn-action btn-audit" onClick={() => { setEditingUserId(null); setEditingUserData({}); }} style={{padding: '4px 8px', fontSize: 12}}><FiEdit2 /></button>
                                    <button className="btn-action btn-audit" onClick={() => { if(window.confirm(`Delete ${c.name}?`)) toast("Delete organization - to be implemented", 'info'); }} style={{padding: '4px 8px', fontSize: 12, color: '#ef4444'}}><FiTrash /></button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p style={{color: 'var(--text-on-black-muted)', padding: 20, textAlign: 'center'}}>No organizations found</p>
                    )}
                  </div>

                  {/* Super Admin: Add Credits */}
                  <div className="settings-section-card" style={{marginTop: 24}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20}}>
                      <div style={{background: `${(branding.primary_color || '#FFD600')}20`, padding: 10, borderRadius: 12, color: branding.primary_color || '#FFD600'}}><FiCreditCard size={20} /></div>
                      <h3 style={{margin: 0, fontSize: 18, fontWeight: 600}}>Add Credits</h3>
                    </div>
                    {creditMsg.text && <div className={creditMsg.type === 'error' ? 'alert-error' : 'alert-success'}>{creditMsg.text}</div>}
                    <form onSubmit={handleAddCredits} className="settings-form">
                      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16}}>
                        <div className="form-group">
                          <label>Select Company</label>
                          <select value={creditCompId} onChange={e => setCreditCompId(e.target.value)} required>
                            <option value="">-- Select Company --</option>
                            {companies.map(c => <option key={c.id} value={c.id}>{c.name} (Current Credits: {c.credits})</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Credits to Add</label>
                          <input type="number" step="0.1" required value={creditsToAdd} onChange={e => setCreditsToAdd(e.target.value)} placeholder="e.g. 50" />
                          <small style={{color: 'var(--text-on-black-muted)', fontSize: 12, marginTop: 4, display: 'block'}}>1 Credit = 10 Signature Requests</small>
                        </div>
                      </div>
                      <div className="form-actions" style={{marginTop: 16}}>
                        <button type="submit" className="btn btn-primary"><FiPlusCircle style={{marginRight: 5}}/> Add Credits</button>
                      </div>
                    </form>
                  </div>

              {/* Super Admin: Create Organization */}
              {isSuperAdmin && (
                <div className="settings-section-card" style={{marginTop: 24}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20}}>
                    <div style={{background: `${(branding.primary_color || '#FFD600')}20`, padding: 10, borderRadius: 12, color: branding.primary_color || '#FFD600'}}><FiBriefcase size={20} /></div>
                    <h3 style={{margin: 0, fontSize: 18, fontWeight: 600}}>Create Organization</h3>
                  </div>
                  {compCreateMsg.text && <div className={compCreateMsg.type === 'error' ? 'alert-error' : 'alert-success'}>{compCreateMsg.text}</div>}
                  <form onSubmit={handleCreateCompany} className="settings-form">
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16}}>
                      <div className="form-group"><label>Company Name</label><input type="text" required value={newCompName} onChange={e => setNewCompName(e.target.value)} placeholder="Acme Corp" /></div>
                      <div className="form-group"><label>Slug (Unique ID)</label><input type="text" required value={newCompSlug} onChange={e => setNewCompSlug(e.target.value)} placeholder="acme-corp" /></div>
                    </div>
                    <div className="form-group"><label>Contact Email</label><input type="email" required value={newCompEmail} onChange={e => setNewCompEmail(e.target.value)} placeholder="contact@acme.com" /></div>
                    <div className="form-group"><label>Address</label><input type="text" required value={newCompAddress} onChange={e => setNewCompAddress(e.target.value)} placeholder="123 Business St" /></div>
                    <div className="form-actions" style={{marginTop: 16}}>
                      <button type="submit" className="btn btn-primary"><FiPlusCircle style={{marginRight: 5}}/> Register Company</button>
                      <button type="button" className="btn btn-secondary" onClick={() => { setNewCompName(""); setNewCompSlug(""); setNewCompEmail(""); setNewCompAddress(""); setCompCreateMsg({type: '', text: ''}); }}>Cancel</button>
                    </div>
                  </form>
                </div>
              )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
